import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import { getAdminUser } from '../lib/adminAuth';
import employeeApi from '../lib/employeeApi';
import { getEmployeeUser } from '../lib/employeeAuth';
import { useStickyState } from '../lib/useStickyState';
import {
  Calculator, TrendingUp, DollarSign, Receipt, FileText, ShoppingCart,
  Plus, Edit, Trash2, Eye, X, Download, Send, Check, AlertCircle,
  Building2, Users, Percent, ArrowUpRight, ArrowDownRight, RefreshCw,
  ChevronDown, ChevronUp, Filter, Search, Calendar, Lock, Ban, CheckSquare, Square,
  CreditCard, CircleDollarSign, Clock, CheckCircle, Copy, BarChart2, PieChart, Banknote,
  Upload, RotateCcw, Mail, Shield, History, User, ArrowRight, MessageCircle, Paperclip, FileSignature
} from 'lucide-react';
import AccountingDashboard from './AccountingDashboard';
import POApprovalPanel, { POSubmitForApprovalModal, POApprovalHistory } from './accounting/POApprovalPanel';
import VendorsManager from './accounting/VendorsManager';
import POCategoriesManager from './accounting/POCategoriesManager';
import RetainerModal from './accounting/RetainerModal';
import POPaymentBadgeCell from './accounting/POPaymentBadgeCell';
import POQuickFilterChips from './accounting/POQuickFilterChips';
import POPaymentProofsDialog from './accounting/POPaymentProofsDialog';
import POAnalytics from './accounting/po-analytics/POAnalytics';
import CategoryReviewBanner from './accounting/category-review/CategoryReviewBanner';
import RetainerGapPanel from './accounting/retainer-gaps/RetainerGapPanel';
import RetainerGapChip from './accounting/retainer-gaps/RetainerGapChip';
import AgreementsHub from './accounting/contracts/AgreementsHub';
import { getPaymentState } from './accounting/poPayment';
import AccountingReports from './AccountingReports';
import CreditNotes from './CreditNotes';
import { formatDate, getTodayLocal } from '../lib/dateUtils';
import PaymentRecording from './PaymentRecording';
import GSTReturns from './GSTReturns';
import ExpensesManager from './ExpensesManager';
import ZohoInvoiceImport from './ZohoInvoiceImport';
import ZohoReportImport, { ZohoImportHistory } from './ZohoReportImport';
import SendInvoiceEmail from './SendInvoiceEmail';
import EmptyState from './EmptyState';
import MobileDataCard from './MobileDataCard';
import StickyFilterChips from './StickyFilterChips';
import { SearchableSelect } from './ui/searchable-select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

// Format currency
const formatCurrency = (amount, currency = 'INR') => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// Metric Card Component
function MetricCard({ title, value, icon: Icon, color, subtext, trend }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-green-50 text-green-600 border-green-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
    pink: 'bg-pink-50 text-pink-600 border-pink-100',
    red: 'bg-red-50 text-red-600 border-red-100',
    cyan: 'bg-cyan-50 text-cyan-600 border-cyan-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    gray: 'bg-white/40 text-neutral-600 border-white/30',
  };

  return (
    <div className={`rounded-xl border-2 p-3 sm:p-4 ${colorClasses[color]}`} data-testid={`metric-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between gap-1 mb-1 sm:mb-2">
        <span className="text-[11px] sm:text-sm font-medium opacity-80 leading-tight">{title}</span>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 opacity-60 flex-shrink-0" />
      </div>
      <p className="text-base sm:text-2xl font-bold break-all">{value}</p>
      {subtext && <p className="text-[10px] sm:text-xs mt-1 opacity-70 leading-tight">{subtext}</p>}
      {trend && (
        <div className={`flex items-center gap-1 mt-1 text-[10px] sm:text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          <span>{Math.abs(trend)}% from last month</span>
        </div>
      )}
    </div>
  );
}

// Sort Icon Component
function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return null;
  return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
}

// Client Breakdown Table
function ClientBreakdownTable({ data, totals }) {
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const sortedData = [...data].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const columns = [
    { key: 'name', label: 'Client Name', align: 'left' },
    { key: 'retainer_value', label: 'Retainer Value', align: 'right' },
    { key: 'non_retainer_invoices', label: 'Non-Retainer', align: 'right' },
    { key: 'total_invoiced', label: 'Total Receivable', align: 'right' },
    { key: 'total_received', label: 'Received', align: 'right' },
    { key: 'purchase_orders', label: 'PO Cost', align: 'right' },
    { key: 'gst', label: 'GST', align: 'right' },
    { key: 'tds', label: 'TDS', align: 'right' },
    { key: 'balance_due', label: 'Outstanding', align: 'right' },
  ];

  return (
    <div className="soft-card overflow-hidden" data-testid="client-breakdown-table">
      {/* Mobile hint */}
      <div className="sm:hidden px-4 py-2 bg-white/40 border-b border-white/30 text-[10px] text-neutral-400 text-center">
        Swipe left to see all columns
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" style={{ minWidth: '800px' }}>
          <thead className="bg-white/40 border-b border-white/40">
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className={`px-3 sm:px-4 py-3 text-${col.align} text-xs sm:text-sm font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-100 transition-colors whitespace-nowrap`}
                >
                  <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                    {col.label}
                    <SortIcon field={col.key} sortField={sortField} sortDir={sortDir} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/30">
            {sortedData.map((client) => (
              <tr key={client._id} className="hover:bg-white/60 transition-colors">
                <td className="px-3 sm:px-4 py-3 font-medium text-[#1a1a2e] whitespace-nowrap text-sm">{client.name}</td>
                <td className="px-3 sm:px-4 py-3 text-right text-neutral-600 whitespace-nowrap text-sm">{formatCurrency(client.retainer_value)}</td>
                <td className="px-3 sm:px-4 py-3 text-right text-neutral-600 whitespace-nowrap text-sm">{formatCurrency(client.non_retainer_invoices)}</td>
                <td className="px-3 sm:px-4 py-3 text-right text-blue-600 font-medium whitespace-nowrap text-sm">{formatCurrency(client.total_invoiced)}</td>
                <td className="px-3 sm:px-4 py-3 text-right text-emerald-600 whitespace-nowrap text-sm">{formatCurrency(client.total_received)}</td>
                <td className="px-3 sm:px-4 py-3 text-right text-neutral-600 whitespace-nowrap text-sm">{formatCurrency(client.purchase_orders)}</td>
                <td className="px-3 sm:px-4 py-3 text-right text-orange-600 whitespace-nowrap text-sm">{formatCurrency(client.gst)}</td>
                <td className="px-3 sm:px-4 py-3 text-right text-purple-600 whitespace-nowrap text-sm">{formatCurrency(client.tds)}</td>
                <td className="px-3 sm:px-4 py-3 text-right font-bold text-red-600 whitespace-nowrap text-sm">{formatCurrency(client.balance_due)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-neutral-100 border-t-2 border-white/40">
            <tr className="font-bold">
              <td className="px-3 sm:px-4 py-3 text-[#1a1a2e] whitespace-nowrap text-sm">TOTAL</td>
              <td className="px-3 sm:px-4 py-3 text-right text-[#1a1a2e] whitespace-nowrap text-sm">{formatCurrency(totals.retainer_value)}</td>
              <td className="px-3 sm:px-4 py-3 text-right text-[#1a1a2e] whitespace-nowrap text-sm">{formatCurrency(totals.non_retainer_invoices)}</td>
              <td className="px-3 sm:px-4 py-3 text-right text-blue-700 whitespace-nowrap text-sm">{formatCurrency(totals.total_invoiced)}</td>
              <td className="px-3 sm:px-4 py-3 text-right text-emerald-700 whitespace-nowrap text-sm">{formatCurrency(totals.total_received)}</td>
              <td className="px-3 sm:px-4 py-3 text-right text-[#1a1a2e] whitespace-nowrap text-sm">{formatCurrency(totals.purchase_orders)}</td>
              <td className="px-3 sm:px-4 py-3 text-right text-orange-700 whitespace-nowrap text-sm">{formatCurrency(totals.gst)}</td>
              <td className="px-3 sm:px-4 py-3 text-right text-purple-700 whitespace-nowrap text-sm">{formatCurrency(totals.tds)}</td>
              <td className="px-3 sm:px-4 py-3 text-right text-red-700 text-base whitespace-nowrap">{formatCurrency(totals.balance_due)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// Inline Salary Edit Row
function SalaryRow({ employee, onUpdate, canEdit = true }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(employee.monthly_salary);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const newSalary = parseFloat(editValue);
    if (isNaN(newSalary) || newSalary < 0) {
      toast.error('Please enter a valid salary amount');
      return;
    }
    
    setSaving(true);
    await onUpdate(newSalary);
    setSaving(false);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(employee.monthly_salary);
    setIsEditing(false);
  };

  return (
    <tr className="hover:bg-white/60 group">
      <td className="px-4 py-3">
        <div>
          <p className="font-medium">{employee.name}</p>
          <p className="text-xs text-neutral-500">{employee.email}</p>
        </div>
      </td>
      <td className="px-4 py-3">{employee.designation || '-'}</td>
      <td className="px-4 py-3">{employee.departments?.join(', ') || '-'}</td>
      <td className="px-4 py-3 text-right font-medium">{formatCurrency(editValue * 12)}</td>
      <td className="px-4 py-3 text-right">
        {isEditing && canEdit ? (
          <div className="flex items-center justify-end gap-2">
            <span className="text-neutral-500">₹</span>
            <Input
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-32 h-8 text-right text-sm font-mono"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') handleCancel();
              }}
            />
            <button
              onClick={handleSave}
              disabled={saving}
              className="p-1 hover:bg-green-50 rounded"
              title="Save"
            >
              <Check className="w-4 h-4 text-green-600" />
            </button>
            <button
              onClick={handleCancel}
              className="p-1 hover:bg-red-50 rounded"
              title="Cancel"
            >
              <X className="w-4 h-4 text-red-600" />
            </button>
          </div>
        ) : (
          <span 
            className={`text-green-600 ${canEdit ? 'cursor-pointer hover:bg-green-50' : ''} px-2 py-1 rounded transition-colors`}
            onClick={() => canEdit && setIsEditing(true)}
            title={canEdit ? "Click to edit" : ""}
          >
            {formatCurrency(employee.monthly_salary)}
          </span>
        )}
      </td>
      {canEdit && (
        <td className="px-4 py-3 text-center">
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="p-1 hover:bg-neutral-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
              title="Edit salary"
            >
              <Edit className="w-4 h-4 text-neutral-500" />
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

// Invoice/PO/Estimate Modal
function DocumentModal({ isOpen, onClose, type, document, clients, onSave, poCategories = [], onCreatePOCategory, vendors = [] }) {
  // Different default items based on document type
  const getDefaultItems = (docType) => {
    if (docType === 'purchase_order') {
      return [{ vendor_name: '', quantity: 1, costing: 0 }];
    }
    return [{ description: '', quantity: 1, unit_price: 0, hsn_code: '998361' }];
  };

  const [formData, setFormData] = useState({
    client_id: '',
    invoice_number: '',
    po_date: '',
    invoice_date: getTodayLocal(), // Default to today
    invoice_type: 'non_retainer',
    items: getDefaultItems(type),
    gst_rate: 18,
    gst_mode: 'exclusive', // PO-only: 'exclusive' (add GST on top) or 'inclusive' (cost includes GST)
    category: '', // PO-only, mandatory
    vendor_name: '', // PO-only, optional master-picked vendor
    tds_rate: 10,
    due_date: getTodayLocal(), // Default to same as invoice date
    valid_until: '',
    notes: '',
    terms: '',
    status: 'draft',
    // Recurring invoice fields
    is_recurring: false,
    recurring_frequency: 'monthly',
    recurring_start_date: '',
    recurring_end_date: '',
    recurring_active: true // NEW: Control if recurring is active
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (document) {
      setFormData({
        client_id: document.client_id || '',
        invoice_number: document.invoice_number || '',
        po_date: document.po_date || '',
        invoice_date: document.invoice_date || document.created_at?.split('T')[0] || getTodayLocal(),
        invoice_type: document.invoice_type || 'non_retainer',
        items: document.items?.length ? document.items.map(item => {
          if (type === 'purchase_order') {
            return {
              vendor_name: item.vendor_name || item.description || '',
              quantity: item.quantity || 1,
              costing: item.costing || item.selling_price || item.unit_price || 0
            };
          }
          return {
            ...item,
            hsn_code: item.hsn_code || '998361'
          };
        }) : getDefaultItems(type),
        gst_rate: document.gst_rate || 18,
        gst_mode: document.gst_mode || 'exclusive',
        category: document.category || '',
        vendor_name: document.vendor_name || '',
        tds_rate: document.tds_rate || 0,
        due_date: document.due_date || '',
        valid_until: document.valid_until || '',
        notes: document.notes || '',
        terms: document.terms || '',
        status: document.status || 'draft',
        is_recurring: document.is_recurring || false,
        recurring_frequency: document.recurring_frequency || 'monthly',
        recurring_start_date: document.recurring_start_date || '',
        recurring_end_date: document.recurring_end_date || '',
        recurring_active: document.recurring_active !== false // Default to true if not set
      });
    } else {
      setFormData({
        client_id: '',
        invoice_number: '',
        po_date: '',
        invoice_date: getTodayLocal(),
        invoice_type: 'non_retainer',
        items: getDefaultItems(type),
        gst_rate: 18,
        gst_mode: 'exclusive',
        category: '',
        tds_rate: 10,
        due_date: getTodayLocal(), // Default to same as invoice date
        valid_until: '',
        notes: '',
        terms: '',
        status: 'draft',
        is_recurring: false,
        recurring_frequency: 'monthly',
        recurring_start_date: '',
        recurring_end_date: '',
        recurring_active: true
      });
    }
  }, [document, isOpen, type]);

  const addItem = () => {
    if (type === 'purchase_order') {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, { vendor_name: '', quantity: 1, costing: 0 }]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        items: [...prev.items, { description: '', quantity: 1, unit_price: 0, hsn_code: '998361' }]
      }));
    }
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { 
          ...item, 
          [field]: (field === 'description' || field === 'hsn_code' || field === 'vendor_name') ? value : parseFloat(value) || 0 
        } : item
      )
    }));
  };

  // Check if client is from Karnataka for GST type determination
  const isKarnatakaClient = () => {
    const client = clients.find(c => c._id === formData.client_id);
    // Mirrors the backend rule: blank state is treated as intra-state (Karnataka)
    const state = (client?.company_state || '').toLowerCase().trim();
    return !state || state === 'karnataka' || state === 'ka';
  };

  const calculateTotals = () => {
    const isKarnataka = isKarnatakaClient();
    
    if (type === 'purchase_order') {
      // POs represent PURCHASES only. Cost = amount. No selling price / margin.
      // GST mode: "exclusive" adds GST on top of the entered cost; "inclusive"
      // treats the entered cost as GST-inclusive and reverse-computes the base.
      const raw = formData.items.reduce((sum, item) => sum + (item.quantity * (item.costing || 0)), 0);
      const rate = Number(formData.gst_rate || 0);
      const isInclusive = (formData.gst_mode || 'exclusive') === 'inclusive';
      const subtotal = isInclusive && rate ? raw / (1 + rate / 100) : raw;
      const gst = isInclusive ? raw - subtotal : subtotal * (rate / 100);
      const total = isInclusive ? raw : subtotal + gst;

      // For POs, use CGST+SGST for Karnataka, IGST for others
      if (isKarnataka) {
        return { subtotal, totalCosting: subtotal, profitMargin: 0, gst, cgst: gst/2, sgst: gst/2, igst: 0, tds: 0, total, isKarnataka };
      } else {
        return { subtotal, totalCosting: subtotal, profitMargin: 0, gst, cgst: 0, sgst: 0, igst: gst, tds: 0, total, isKarnataka };
      }
    } else {
      const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
      const gst = subtotal * (formData.gst_rate / 100);
      const tds = type === 'invoice' ? subtotal * (formData.tds_rate / 100) : 0;
      const total = subtotal + gst - tds;
      
      // For Invoices/Estimates: Karnataka = CGST+SGST, Others = IGST
      if (isKarnataka) {
        return { subtotal, gst, cgst: gst/2, sgst: gst/2, igst: 0, tds, total, totalCosting: 0, profitMargin: 0, isKarnataka };
      } else {
        return { subtotal, gst, cgst: 0, sgst: 0, igst: gst, tds, total, totalCosting: 0, profitMargin: 0, isKarnataka };
      }
    }
  };

  const { subtotal, gst, cgst, sgst, igst, tds, total, totalCosting, profitMargin, isKarnataka } = calculateTotals();

  const handleSubmit = async () => {
    // PO-only: category + vendor are mandatory and gst_mode must be set
    if (type === 'purchase_order') {
      if (!formData.category || !formData.category.trim()) {
        toast.error('Please pick a PO category before saving');
        return;
      }
      if (!formData.vendor_name || !formData.vendor_name.trim()) {
        toast.error('Vendor is required. Pick from the directory or add a new vendor.');
        return;
      }
      if (!['exclusive', 'inclusive'].includes(formData.gst_mode || 'exclusive')) {
        toast.error('Choose whether the cost is GST-inclusive or exclusive');
        return;
      }
    }
    setLoading(true);
    try {
      if (type === 'purchase_order') {
        await onSave({
          ...formData,
          items: formData.items.map(item => ({
            ...item,
            amount: item.quantity * (item.costing || 0)
          }))
        });
      } else {
        await onSave({
          ...formData,
          items: formData.items.map(item => ({
            ...item,
            amount: item.quantity * item.unit_price
          }))
        });
      }
      onClose();
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const typeLabels = {
    invoice: 'Tax Invoice',
    estimate: 'Estimate',
    purchase_order: 'Purchase Order'
  };

  const statusOptions = {
    invoice: ['draft', 'sent', 'paid', 'partially_paid', 'overdue', 'cancelled'],
    estimate: ['draft', 'sent', 'accepted', 'rejected', 'converted'],
    purchase_order: ['draft', 'sent', 'approved', 'rejected', 'completed']
  };

  // Get selected client details
  const selectedClient = clients.find(c => c._id === formData.client_id);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-4xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto p-0 gap-0 bg-white rounded-t-2xl sm:rounded-xl max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:max-w-full max-sm:data-[state=open]:slide-in-from-bottom-full">
        {/* Header - Responsive */}
        <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-white/40 p-4 sm:p-6 sticky top-0 z-10">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            {/* Company Info - Hidden on small screens, shown on medium+ */}
            <div className="hidden sm:flex items-start gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-900 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-base sm:text-lg">H</span>
              </div>
              <div>
                <h3 className="font-bold text-[#1a1a2e] text-sm sm:text-base">Half Eaten Donut Private Limited</h3>
                <p className="text-xs sm:text-sm text-neutral-500">2, 1st Floor, Wood Street, Ashok Nagar</p>
                <p className="text-xs sm:text-sm text-neutral-500">Bengaluru 560025</p>
                <p className="text-xs text-neutral-400 mt-1">GSTIN: 29AAFCH4136C1ZT</p>
              </div>
            </div>
            {/* Document Title - Full width on mobile */}
            <div className="text-left sm:text-right">
              <h2 className="text-xl sm:text-2xl font-bold text-[#1a1a2e]">{typeLabels[type]}</h2>
              <p className="text-xs sm:text-sm text-neutral-500 mt-1">
                {document ? `Editing ${document.invoice_number || document.po_number || document.estimate_number || 'document'}` : 'New Document'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Bill To Section - Responsive Grid */}
          <div className="bg-white/40 rounded-xl border border-white/40 p-4 sm:p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {/* Left Column - Client Selection */}
              <div>
                <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 sm:mb-3">
                  {type === 'purchase_order' ? 'Client Details' : 'Bill To'}
                </h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">Select Client *</label>
                    <Select value={formData.client_id} onValueChange={(v) => setFormData(prev => ({ ...prev, client_id: v }))}>
                      <SelectTrigger className="bg-white border-white/40 focus:border-teal-500 focus:ring-teal-500" data-testid="doc-client-select">
                        {/* Never leak a raw ObjectId if `clients` hasn't loaded yet or
                            the client is archived and filtered out. We resolve the
                            display name from (in order): the loaded clients list,
                            the PO document's cached client_name, then a friendly
                            loading placeholder. This is the permanent guard against
                            the "client name shows as code" flash. */}
                        {formData.client_id ? (
                          <span className="flex items-center gap-2 min-w-0">
                            <Building2 className="w-4 h-4 text-neutral-400 shrink-0" />
                            <span className="truncate">
                              {(selectedClient?.company_name || selectedClient?.name)
                                || document?.client_name
                                || 'Loading client…'}
                            </span>
                          </span>
                        ) : (
                          <SelectValue placeholder="Choose a client..." />
                        )}
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {[...clients].sort((a, b) => {
                          if (a.is_archived !== b.is_archived) return a.is_archived ? 1 : -1;
                          return (a.company_name || a.name || '').localeCompare(b.company_name || b.name || '');
                        }).map(client => (
                          <SelectItem key={client._id} value={client._id}>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-neutral-400" />
                              <span>{client.company_name || client.name}</span>
                              {client.is_archived && <span className="text-[10px] px-1 py-0.5 bg-neutral-100 text-neutral-500 rounded">archived</span>}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {selectedClient && (
                    <div className="bg-white rounded-xl p-3 border border-white/40">
                      <p className="font-semibold text-[#1a1a2e]">{selectedClient.company_name || selectedClient.name}</p>
                      <p className="text-sm text-neutral-500">{selectedClient.company_address || selectedClient.address || '-'}</p>
                      {selectedClient.company_state && (
                        <p className="text-sm text-neutral-500">{selectedClient.company_state} {selectedClient.company_pin_code && `- ${selectedClient.company_pin_code}`}</p>
                      )}
                      <p className="text-xs text-neutral-400 mt-1">GSTIN: {selectedClient.gstin || '-'}</p>
                      {selectedClient.company_state && (
                        <p className={`text-xs mt-1 px-2 py-0.5 rounded inline-block ${
                          selectedClient.company_state.toLowerCase() === 'karnataka' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {selectedClient.company_state.toLowerCase() === 'karnataka' ? 'Intra-state (CGST+SGST)' : 'Inter-state (IGST)'}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Document Details */}
              <div>
                <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">Document Details</h4>
                <div className="space-y-3">
                  {/* Invoice Number - Editable */}
                  {type === 'invoice' && (
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 mb-1">
                        <FileText className="w-3 h-3 inline mr-1" />
                        Invoice Number
                      </label>
                      <Input
                        type="text"
                        value={formData.invoice_number || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                        placeholder={document ? document.invoice_number : 'Auto-generated if empty'}
                        className="bg-white border-white/40 focus:border-teal-500 focus:ring-teal-500"
                        data-testid="invoice-number-input"
                      />
                      <p className="text-xs text-neutral-400 mt-1">{document ? 'Edit the invoice number' : 'Leave empty for auto-generated number'}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {type === 'invoice' && (
                      <div>
                        <label className="block text-xs font-medium text-neutral-600 mb-1">Invoice Type</label>
                        <Select value={formData.invoice_type} onValueChange={(v) => setFormData(prev => ({ ...prev, invoice_type: v }))}>
                          <SelectTrigger className="bg-white border-white/40 focus:border-teal-500 focus:ring-teal-500">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="retainer">
                              <div className="flex items-center gap-2">
                                <RefreshCw className="w-3 h-3 text-purple-500" />
                                Retainer
                              </div>
                            </SelectItem>
                            <SelectItem value="non_retainer">
                              <div className="flex items-center gap-2">
                                <FileText className="w-3 h-3 text-blue-500" />
                                Non-Retainer
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 mb-1">Status</label>
                      <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                        <SelectTrigger className="bg-white border-white/40 focus:border-teal-500 focus:ring-teal-500">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions[type]?.map(status => (
                            <SelectItem key={status} value={status}>
                              <span className="capitalize">{status.replace('_', ' ')}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Invoice Date - Editable */}
                  {type === 'invoice' && (
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 mb-1">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        Invoice Date
                      </label>
                      <Input
                        type="date"
                        value={formData.invoice_date}
                        onChange={(e) => {
                          const newInvoiceDate = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            invoice_date: newInvoiceDate,
                            // Auto-sync due_date if it was same as invoice_date (user hasn't manually changed it)
                            due_date: prev.due_date === prev.invoice_date || !prev.due_date ? newInvoiceDate : prev.due_date
                          }));
                        }}
                        className="bg-white border-white/40 focus:border-teal-500 focus:ring-teal-500"
                        data-testid="invoice-date-input"
                      />
                      <p className="text-xs text-neutral-400 mt-1">Date shown on the invoice</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {type === 'estimate' ? 'Valid Until' : type === 'purchase_order' ? 'PO Date' : 'Due Date'}
                    </label>
                    <Input
                      type="date"
                      value={type === 'estimate' ? formData.valid_until : type === 'purchase_order' ? formData.po_date : formData.due_date}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        [type === 'estimate' ? 'valid_until' : type === 'purchase_order' ? 'po_date' : 'due_date']: e.target.value
                      }))}
                      className="bg-white border-white/40 focus:border-teal-500 focus:ring-teal-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Line Items Section - Mobile Optimized */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Line Items</h4>
              <Button type="button" variant="outline" size="sm" onClick={addItem} className="border-teal-500 text-teal-600 hover:bg-teal-50">
                <Plus className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Add Item</span>
              </Button>
            </div>
            
            {/* Desktop Table Header - Hidden on Mobile */}
            <div className="hidden sm:block bg-neutral-100 rounded-t-lg border border-white/40 border-b-0">
              {type === 'purchase_order' ? (
                <div className="flex items-center px-4 py-2.5 text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                  <div className="w-[4%] text-center">#</div>
                  <div className="w-[52%]">Vendor / Item Name</div>
                  <div className="w-[12%] text-center">Qty</div>
                  <div className="w-[16%] text-right">Cost Price (₹) *</div>
                  <div className="w-[12%] text-right">Amount</div>
                  <div className="w-[4%]"></div>
                </div>
              ) : (
                <div className="flex items-center px-4 py-2.5 text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                  <div className="w-[4%] text-center">#</div>
                  <div className="w-[32%]">Item & Description</div>
                  <div className="w-[12%]">HSN/SAC</div>
                  <div className="w-[8%] text-center">Qty</div>
                  <div className="w-[20%] text-right">Rate (₹)</div>
                  <div className="w-[20%] text-right">Amount</div>
                  <div className="w-[4%]"></div>
                </div>
              )}
            </div>
            
            {/* Items - Desktop Row / Mobile Card Layout */}
            <div className="border border-white/40 sm:rounded-b-lg rounded-xl divide-y divide-white/30 bg-white">
              {formData.items.map((item, index) => (
                <div key={index} className="p-3 sm:p-0">
                  {/* Mobile Card Layout */}
                  <div className="sm:hidden space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-neutral-500">Item #{index + 1}</span>
                      {formData.items.length > 1 && (
                        <button onClick={() => removeItem(index)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {type === 'purchase_order' ? (
                      <>
                        <Input
                          placeholder="Vendor / Item name..."
                          value={item.vendor_name || item.description || ''}
                          onChange={(e) => updateItem(index, 'vendor_name', e.target.value)}
                          list={type === 'purchase_order' ? 'po-vendor-suggestions' : undefined}
                          className="border-white/40 text-sm"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-neutral-500 mb-1 block">Qty</label>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                              className="border-white/40 text-sm text-center"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-neutral-500 mb-1 block">Cost (₹)</label>
                            <Input
                              type="number"
                              value={item.costing || 0}
                              onChange={(e) => updateItem(index, 'costing', e.target.value)}
                              className="border-white/40 text-sm text-right font-mono"
                            />
                          </div>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-white/30">
                          <span className="text-xs text-neutral-500">Amount:</span>
                          <span className="font-semibold text-[#1a1a2e]">{formatCurrency(item.quantity * (item.costing || 0))}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <Input
                          placeholder="Service description..."
                          value={item.description || ''}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          className="border-white/40 text-sm"
                        />
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-xs text-neutral-500 mb-1 block">HSN</label>
                            <Input
                              value={item.hsn_code || '998361'}
                              onChange={(e) => updateItem(index, 'hsn_code', e.target.value)}
                              className="border-white/40 text-sm"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-neutral-500 mb-1 block">Qty</label>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                              className="border-white/40 text-sm text-center"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-neutral-500 mb-1 block">Rate (₹)</label>
                            <Input
                              type="number"
                              value={item.unit_price || 0}
                              onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                              className="border-white/40 text-sm text-right font-mono"
                            />
                          </div>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-white/30">
                          <span className="text-xs text-neutral-500">Amount:</span>
                          <span className="font-semibold text-[#1a1a2e]">{formatCurrency(item.quantity * (item.unit_price || 0))}</span>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Desktop Row Layout */}
                  <div className="hidden sm:flex items-center px-4 py-3 hover:bg-white/60 transition-colors group">
                    <div className="w-[4%] text-center text-sm font-medium text-neutral-500">
                      {index + 1}
                    </div>
                  
                  {type === 'purchase_order' ? (
                    <>
                      {/* Purchase Order Layout - Cost only (purchases) */}
                      <div className="w-[52%] pr-2">
                        <Input
                          placeholder="Vendor / Item name..."
                          value={item.vendor_name || item.description || ''}
                          onChange={(e) => updateItem(index, 'vendor_name', e.target.value)}
                          list="po-vendor-suggestions"
                          className="border-0 bg-transparent focus:bg-white focus:border-white/40 focus:ring-1 focus:ring-teal-500 placeholder:text-neutral-400 text-sm"
                        />
                      </div>
                      <div className="w-[12%] pr-2">
                        <Input
                          type="number"
                          placeholder="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          className="border-0 bg-transparent focus:bg-white focus:border-white/40 focus:ring-1 focus:ring-teal-500 text-center text-sm"
                        />
                      </div>
                      <div className="w-[16%] pr-2">
                        <Input
                          type="number"
                          placeholder="0.00"
                          value={item.costing || 0}
                          onChange={(e) => updateItem(index, 'costing', e.target.value)}
                          className="border-0 bg-transparent focus:bg-white focus:border-white/40 focus:ring-1 focus:ring-teal-500 text-right text-sm font-mono"
                        />
                      </div>
                      <div className="w-[12%] flex items-center justify-end">
                        <span className="font-semibold text-[#1a1a2e] font-mono text-sm">
                          {formatCurrency(item.quantity * (item.costing || 0))}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Invoice/Estimate Layout */}
                      <div className="w-[32%] pr-2">
                        <Input
                          placeholder="Service description..."
                          value={item.description || ''}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          className="border-0 bg-transparent focus:bg-white focus:border-white/40 focus:ring-1 focus:ring-teal-500 placeholder:text-neutral-400 text-sm"
                        />
                      </div>
                      <div className="w-[12%] pr-2">
                        <Input
                          placeholder="998361"
                          value={item.hsn_code || '998361'}
                          onChange={(e) => updateItem(index, 'hsn_code', e.target.value)}
                          className="border-0 bg-transparent focus:bg-white focus:border-white/40 focus:ring-1 focus:ring-teal-500 text-sm"
                        />
                      </div>
                      <div className="w-[8%] pr-2">
                        <Input
                          type="number"
                          placeholder="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          className="border-0 bg-transparent focus:bg-white focus:border-white/40 focus:ring-1 focus:ring-teal-500 text-center text-sm"
                        />
                      </div>
                      <div className="w-[20%] pr-2">
                        <Input
                          type="number"
                          placeholder="0.00"
                          value={item.unit_price || 0}
                          onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                          className="border-0 bg-transparent focus:bg-white focus:border-white/40 focus:ring-1 focus:ring-teal-500 text-right text-sm font-mono"
                        />
                      </div>
                      <div className="w-[20%] flex items-center justify-end">
                        <span className="font-semibold text-[#1a1a2e] font-mono text-sm">
                          {formatCurrency(item.quantity * (item.unit_price || 0))}
                        </span>
                      </div>
                    </>
                  )}
                  
                  <div className="w-[4%] flex justify-center">
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded transition-opacity"
                      >
                        <X className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                  </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tax & Totals Section - Responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Left - Tax Settings & Notes */}
            <div className="space-y-4">
              {/* PO-only: Category + GST mode toggle. Category is mandatory. */}
              {type === 'purchase_order' && (
                <div className="glass-card p-4 space-y-4">
                  <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Purchase Details
                  </h4>
                  {/* Vendor picker — mandatory. Legacy POs without a vendor
                      surface a soft "please pick one" banner on edit so the
                      user can't save until they add or select. */}
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                      Vendor <span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      value={formData.vendor_name || ''}
                      onChange={(v) => setFormData(prev => {
                        // If line items are empty vendor_name, pre-fill them
                        // with the picked vendor for a nice ergonomic default.
                        const nextItems = (prev.items || []).map(it =>
                          (!it.vendor_name && !it.description) ? { ...it, vendor_name: v } : it
                        );
                        return { ...prev, vendor_name: v, items: nextItems };
                      })}
                      placeholder="Pick a vendor from your directory..."
                      triggerClassName={`bg-white/40 border-white/40 ${!formData.vendor_name ? 'ring-1 ring-red-200' : ''}`}
                      options={vendors.map(v => ({
                        value: v.name,
                        label: v.contact_person ? `${v.name} · ${v.contact_person}` : v.name,
                      }))}
                      data-testid="po-vendor-select"
                    />
                    {!formData.vendor_name ? (
                      <p className="text-[11px] text-red-500 mt-1">
                        {document ? 'This legacy PO has no vendor — pick one or add a new vendor before saving.' : 'Every PO must be tagged with a vendor. Manage the list under Payables → Vendors.'}
                      </p>
                    ) : (
                      <p className="text-[11px] text-neutral-500 mt-1">Manage the vendor list under Payables → Vendors.</p>
                    )}
                  </div>
                  {/* Category picker */}
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <SearchableSelect
                          value={formData.category}
                          onChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                          placeholder="Pick a category..."
                          triggerClassName={`bg-white/40 border-white/40 ${!formData.category ? 'ring-1 ring-red-200' : ''}`}
                          options={poCategories.map(c => ({ value: c.name, label: c.name }))}
                          data-testid="po-category-select"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={async () => {
                          const name = window.prompt('New PO category name:');
                          if (!name || !name.trim()) return;
                          try {
                            const created = await onCreatePOCategory?.(name.trim());
                            if (created?.name) setFormData(prev => ({ ...prev, category: created.name }));
                          } catch (e) {
                            // handler surfaces its own toast
                          }
                        }}
                        data-testid="po-category-add"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    {!formData.category && (
                      <p className="text-[11px] text-red-500 mt-1">Every PO must be tagged with a category.</p>
                    )}
                  </div>
                  {/* GST mode toggle */}
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">GST Handling</label>
                    <div className="inline-flex bg-white/60 rounded-xl p-1 border border-white/40" role="tablist">
                      {[
                        { v: 'exclusive', label: 'Exclusive', hint: 'Add GST on top' },
                        { v: 'inclusive', label: 'Inclusive', hint: 'Cost already includes GST' },
                      ].map(opt => {
                        const active = (formData.gst_mode || 'exclusive') === opt.v;
                        return (
                          <button
                            key={opt.v}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, gst_mode: opt.v }))}
                            className={`px-4 py-1.5 text-sm rounded-lg transition-all ${active ? 'bg-teal-500 text-white shadow-sm' : 'text-neutral-600 hover:text-neutral-900'}`}
                            data-testid={`po-gst-mode-${opt.v}`}
                            title={opt.hint}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-neutral-500 mt-1">
                      {(formData.gst_mode || 'exclusive') === 'inclusive'
                        ? 'The cost you enter is treated as GST-inclusive; base is reverse-computed.'
                        : 'The cost you enter is pre-GST; GST will be added on top.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Tax Rates */}
              <div className="glass-card p-4">
                <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Percent className="w-4 h-4" /> Tax Configuration
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">GST Rate (%)</label>
                    <Select 
                      value={String(formData.gst_rate)} 
                      onValueChange={(v) => setFormData(prev => ({ ...prev, gst_rate: parseFloat(v) }))}
                    >
                      <SelectTrigger className="bg-white/40 border-white/40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0% (Exempt)</SelectItem>
                        <SelectItem value="5">5%</SelectItem>
                        <SelectItem value="12">12%</SelectItem>
                        <SelectItem value="18">18%</SelectItem>
                        <SelectItem value="28">28%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {type === 'invoice' && (
                    <div>
                      <label className="block text-xs font-medium text-neutral-600 mb-1">TDS Rate (%)</label>
                      <Select 
                        value={String(formData.tds_rate)} 
                        onValueChange={(v) => setFormData(prev => ({ ...prev, tds_rate: parseFloat(v) }))}
                      >
                        <SelectTrigger className="bg-white/40 border-white/40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0% (No TDS)</SelectItem>
                          <SelectItem value="1">1%</SelectItem>
                          <SelectItem value="2">2%</SelectItem>
                          <SelectItem value="5">5%</SelectItem>
                          <SelectItem value="10">10%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>

              {/* Recurring Options */}
              {type === 'invoice' && (
                <div className="glass-card p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="is_recurring"
                        checked={formData.is_recurring}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_recurring: e.target.checked, recurring_active: e.target.checked }))}
                        className="w-4 h-4 rounded border-white/40 text-teal-600 focus:ring-teal-500"
                      />
                      <label htmlFor="is_recurring" className="text-sm font-medium text-neutral-700 flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-purple-500" />
                        Make this a recurring invoice
                      </label>
                    </div>
                    
                    {/* Recurring Status Toggle (for existing recurring invoices) */}
                    {formData.is_recurring && document && (
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${formData.recurring_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {formData.recurring_active ? '● Active' : '○ Stopped'}
                        </span>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, recurring_active: !prev.recurring_active }))}
                          className={`text-xs px-3 py-1 rounded-xl transition-colors ${
                            formData.recurring_active 
                              ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200' 
                              : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
                          }`}
                        >
                          {formData.recurring_active ? 'Stop Recurring' : 'Start Recurring'}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {formData.is_recurring && (
                    <div className="space-y-3 mt-3 pt-3 border-t border-white/30">
                      {/* Info about recurring */}
                      <div className="bg-purple-50 border border-purple-200 rounded-xl p-3">
                        <p className="text-xs text-purple-700">
                          <strong>Note:</strong> Recurring invoices will be auto-generated on the <strong>1st of every month</strong> (or as per frequency) 
                          unless manually stopped. You can start/stop recurring at any time.
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-neutral-600 mb-1">Frequency</label>
                          <Select value={formData.recurring_frequency} onValueChange={(v) => setFormData(prev => ({ ...prev, recurring_frequency: v }))}>
                            <SelectTrigger className="bg-white/40 border-white/40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="monthly">Monthly (1st of month)</SelectItem>
                              <SelectItem value="quarterly">Quarterly</SelectItem>
                              <SelectItem value="yearly">Yearly</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-neutral-600 mb-1">Start Date</label>
                          <Input
                            type="date"
                            value={formData.recurring_start_date}
                            onChange={(e) => setFormData(prev => ({ ...prev, recurring_start_date: e.target.value }))}
                            className="bg-white/40 border-white/40 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-neutral-600 mb-1">End Date (Optional)</label>
                          <Input
                            type="date"
                            value={formData.recurring_end_date}
                            onChange={(e) => setFormData(prev => ({ ...prev, recurring_end_date: e.target.value }))}
                            className="bg-white/40 border-white/40 text-sm"
                            placeholder="Leave empty for no end"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2">Notes</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add any notes for this document..."
                  rows={3}
                  className="bg-white border-white/40 focus:border-teal-500 focus:ring-teal-500 resize-none"
                />
              </div>
            </div>

            {/* Right - Totals Summary */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-5 text-white">
              <h4 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-4">Amount Summary</h4>
              
              <div className="space-y-3">
                {/* For POs — pure purchase document: only cost total shown */}
                {type === 'purchase_order' && (
                  <div className="bg-orange-500/10 rounded-xl p-3 mb-3 border border-orange-400/40">
                    <p className="text-xs text-orange-300 uppercase tracking-wider mb-2 font-semibold">Vendor Payable · Cost</p>
                    <div className="flex justify-between items-center">
                      <span className="text-neutral-300 text-sm">Total Cost Price</span>
                      <span className="font-bold text-2xl text-orange-400">{formatCurrency(totalCosting)}</span>
                    </div>
                    <p className="text-[10px] text-neutral-500 italic mt-1.5">Approved POs can be converted into a customer invoice in one click.</p>
                  </div>
                )}

                {type !== 'purchase_order' && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-neutral-400">Sub Total</span>
                  <span className="font-medium">{formatCurrency(subtotal)}</span>
                </div>
                )}
                
                {/* GST Display - CGST+SGST for Karnataka, IGST for others */}
                <div className="border-t border-gray-700 pt-3 space-y-2">
                  {isKarnataka ? (
                    <>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-neutral-400">CGST ({formData.gst_rate / 2}%)</span>
                        <span className="font-medium text-blue-400">{formatCurrency(cgst)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-neutral-400">SGST ({formData.gst_rate / 2}%)</span>
                        <span className="font-medium text-blue-400">{formatCurrency(sgst)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-neutral-400">IGST ({formData.gst_rate}%)</span>
                      <span className="font-medium text-blue-400">{formatCurrency(igst)}</span>
                    </div>
                  )}
                  {/* Show state indicator */}
                  {formData.client_id && (
                    <p className="text-xs text-neutral-500 mt-1">
                      {isKarnataka ? '📍 Intra-state (Karnataka)' : '📍 Inter-state supply'}
                    </p>
                  )}
                </div>
                
                {type === 'invoice' && formData.tds_rate > 0 && (
                  <div className="flex justify-between items-center text-sm border-t border-gray-700 pt-3">
                    <span className="text-neutral-400">TDS Withheld ({formData.tds_rate}%)</span>
                    <span className="font-medium text-amber-400">({formatCurrency(tds)})</span>
                  </div>
                )}
                
                <div className="border-t border-gray-600 pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-neutral-300">Total</span>
                    <span className="text-2xl font-bold text-teal-400">{formatCurrency(total)}</span>
                  </div>
                  <p className="text-xs text-neutral-500 mt-2 text-right">
                    {total > 0 && `Rupees ${numberToWords(Math.round(total))} Only`}
                  </p>
                </div>
              </div>
              
              {/* Balance Due Highlight */}
              <div className="mt-4 bg-gray-800/50 rounded-xl p-3 border border-gray-700">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-neutral-400">{type === 'purchase_order' ? 'Customer Total' : 'Balance Due'}</span>
                  <span className={`text-xl font-bold ${total > 0 ? 'text-pink-400' : 'text-green-400'}`}>
                    {formatCurrency(total)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions - Mobile Responsive */}
        <div className="bg-white/40 border-t border-white/40 px-4 sm:px-6 py-4 sticky bottom-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-xs text-neutral-500 hidden sm:block">
              All fields marked with * are required
            </div>
            <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
              <Button variant="outline" onClick={onClose} className="flex-1 sm:flex-none border-white/40 hover:bg-neutral-100">
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={loading || !formData.client_id}
                className="flex-1 sm:flex-none bg-teal-600 hover:bg-teal-700 text-white shadow-lg shadow-teal-500/25"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    <span className="hidden sm:inline">Saving...</span>
                    <span className="sm:hidden">...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{document ? 'Update' : 'Create'} {typeLabels[type]}</span>
                    <span className="sm:hidden">{document ? 'Update' : 'Create'}</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper function for number to words
function numberToWords(num) {
  if (num === 0) return 'Zero';
  
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  const convert = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
    if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };
  
  return convert(Math.abs(Math.round(num)));
}

// RetainerModal + POPaymentBadgeCell were extracted to
// /app/frontend/src/components/accounting/ in the Feb-2026 monolith split.


export default function AccountingPage({ 
  mode = 'admin', 
  employeeRole = 'regular',  // Default to regular (Boss privileges eliminated)
  permissions: permissionsProp = null,
  currentEmployeeId = null
}) {
  // Default permissions based on mode and role
  const getDefaultPermissions = () => {
    if (mode === 'admin') {
      return {
        canViewDashboard: true,
        canViewInvoices: true,
        canViewEstimates: true,
        canViewPOs: true,
        canViewRetainers: true,
        canViewSalaries: true,
        canCreateInvoices: true,
        canCreateEstimates: true,
        canCreatePOs: true,
        canEditInvoices: true,
        canEditEstimates: true,
        canEditPOs: true,
        canDeleteInvoices: true,
        canDeleteEstimates: true,
        canDeletePOs: true,
        canEditSalaries: true,
        canEditRetainers: true,
        canDownload: true,
        viewOwnPOsOnly: false,
      };
    } else {
      // ALL employees get uniform PO-only access (Boss privileges eliminated)
      // This includes both 'boss' and 'regular' employeeRole values
      return {
        canViewDashboard: false,
        canViewInvoices: false,
        canViewEstimates: false,
        canViewPOs: true,
        canViewRetainers: false,
        canViewSalaries: false,
        canCreateInvoices: false,
        canCreateEstimates: false,
        canCreatePOs: true,
        canEditInvoices: false,
        canEditEstimates: false,
        canEditPOs: true, // Own POs only
        canDeleteInvoices: false,
        canDeleteEstimates: false,
        canDeletePOs: false,
        canEditSalaries: false,
        canEditRetainers: false,
        canDownload: true, // Own POs only
        viewOwnPOsOnly: true,
      };
    }
  };
  
  const permissions = permissionsProp || getDefaultPermissions();
  
  // Select API based on mode
  const api = mode === 'admin' ? adminApi : employeeApi;
  const apiPrefix = mode === 'admin' ? '/api/admin' : '/api/employee';
  // Per-user scope for sticky-state so filters/tabs survive reloads but
  // don't leak between different accounts on the same browser.
  const stickyScope = React.useMemo(() => {
    const u = mode === 'admin' ? getAdminUser() : getEmployeeUser();
    return u?.email || u?._id || 'anon';
  }, [mode]);

  // Set initial tab based on permissions
  const getInitialTab = () => {
    if (permissions.canViewDashboard) return 'financial_dashboard';
    if (permissions.canViewPOs) return 'purchase_orders';
    if (permissions.canViewInvoices) return 'invoices';
    return 'purchase_orders';
  };

  const [activeTab, setActiveTab] = useStickyState('accounting.activeTab', getInitialTab(), stickyScope);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Navigation states for drill-down
  const [reportSubView, setReportSubView] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [allClientsForFilter, setAllClientsForFilter] = useState([]);
  const [clientFilterSearch, setClientFilterSearch] = useState('');
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);

  // Audit Trail states
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilters, setAuditFilters] = useState({ action: '', entity_type: '', user_email: '', date_from: '', date_to: '' });
  const [auditSummary, setAuditSummary] = useState(null);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [salaries, setSalaries] = useState(null);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  
  // Modal states
  const [showDocModal, setShowDocModal] = useState(false);  const [docModalType, setDocModalType] = useState('invoice');
  const [editingDoc, setEditingDoc] = useState(null);
  const [poCategories, setPoCategories] = useState([]);
  const [submitPO, setSubmitPO] = useState(null);  // PO to submit for approval
  const [expandedPOId, setExpandedPOId] = useState(null);  // PO row currently showing timeline
  const [selectedPOIds, setSelectedPOIds] = useState([]);  // bulk-selected PO ids
  const [vendorSuggestions, setVendorSuggestions] = useState([]);  // typeahead for PO line items
  const [showRetainerModal, setShowRetainerModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showZohoImport, setShowZohoImport] = useState(false);
  const [showZohoReportImport, setShowZohoReportImport] = useState(false);
  const [showImportHistory, setShowImportHistory] = useState(false);
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [emailEstimate, setEmailEstimate] = useState(null);
  // Convert estimate → invoice one-click
  const handleConvertEstimateToInvoice = async (est) => {
    if (!est?._id) return;
    if (!window.confirm(`Convert estimate ${est.estimate_number || ''} into a draft invoice?\nAll line items, GST split and totals will carry over and the estimate will be marked as converted.`)) return;
    try {
      const res = await api.post(`${apiPrefix}/accounting/estimates/${est._id}/convert-to-invoice`);
      toast.success(`Invoice ${res.data?.invoice_number || 'created'} drafted from ${est.estimate_number || 'estimate'}`);
      // Optimistic status flip so the row shows the new state without a full reload
      setEstimates(prev => prev.map(e => e._id === est._id ? { ...e, status: 'converted', converted_to_invoice_id: res.data?._id } : e));
      fetchInvoices?.(false, { silent: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to convert estimate');
    }
  };
  
  // Invoice number editing states
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [editingInvoiceNumber, setEditingInvoiceNumber] = useState('');
  const [invoiceNumberError, setInvoiceNumberError] = useState(null);
  const [savingInvoiceNumber, setSavingInvoiceNumber] = useState(false);

  // Date preset helper
  const getDatePresetRange = useCallback((preset) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0-indexed
    const fyStartYear = month >= 3 ? year : year - 1;
    const pad = (n) => String(n).padStart(2, '0');

    switch (preset) {
      case 'current_fy':
        return { from: `${fyStartYear}-04-01`, to: `${fyStartYear + 1}-03-31` };
      case 'current_cy':
        return { from: `${year}-01-01`, to: `${year}-12-31` };
      case 'last_month': {
        const lm = month === 0 ? 11 : month - 1;
        const lmYear = month === 0 ? year - 1 : year;
        const lastDay = new Date(lmYear, lm + 1, 0).getDate();
        return { from: `${lmYear}-${pad(lm + 1)}-01`, to: `${lmYear}-${pad(lm + 1)}-${pad(lastDay)}` };
      }
      case 'last_quarter': {
        const cq = Math.floor(month / 3);
        const lq = cq === 0 ? 3 : cq - 1;
        const lqYear = cq === 0 ? year - 1 : year;
        const lqStartMonth = lq * 3;
        const lqEndMonth = lqStartMonth + 2;
        const lqLastDay = new Date(lqYear, lqEndMonth + 1, 0).getDate();
        return { from: `${lqYear}-${pad(lqStartMonth + 1)}-01`, to: `${lqYear}-${pad(lqEndMonth + 1)}-${pad(lqLastDay)}` };
      }
      case 'last_fy':
        return { from: `${fyStartYear - 1}-04-01`, to: `${fyStartYear}-03-31` };
      case 'all_time':
        return { from: '', to: '' };
      default:
        return { from: '', to: '' };
    }
  }, []);

  const defaultFYRange = useMemo(() => getDatePresetRange('current_fy'), [getDatePresetRange]);

  // Invoice filter and sort states (sticky per user)
  const [invoiceDatePreset, setInvoiceDatePreset] = useStickyState('accounting.invoiceDatePreset', 'current_fy', stickyScope);
  const [invoiceFilters, setInvoiceFilters] = useStickyState('accounting.invoiceFilters', {
    status: 'all',
    type: 'all',
    client: 'all',
    dateFrom: defaultFYRange.from,
    dateTo: defaultFYRange.to,
    search: ''
  }, stickyScope);
  const [invoiceSortField, setInvoiceSortField] = useStickyState('accounting.invoiceSortField', 'invoice_date', stickyScope);
  const [invoiceSortDir, setInvoiceSortDir] = useStickyState('accounting.invoiceSortDir', 'desc', stickyScope);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceLimit] = useState(50);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [invoiceTotalPages, setInvoiceTotalPages] = useState(1);
  const [invoiceHasMore, setInvoiceHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [invoiceServerMetrics, setInvoiceServerMetrics] = useState(null);
  const [showInvoiceFilters, setShowInvoiceFilters] = useState(false);

  // PO filter states (sticky per user) — mirrors the invoice filter shape
  const [poFilters, setPoFilters] = useStickyState('accounting.poFilters', {
    status: 'all',
    approval_status: 'all',
    category: 'all',
    vendor: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  }, stickyScope);
  const [showPOFilters, setShowPOFilters] = useState(false);
  const [poPaymentChip, setPoPaymentChip] = useStickyState('accounting.poPaymentChip', 'all', stickyScope);
  const [proofPO, setProofPO] = useState(null);
  const openInvoiceInEditor = (inv) => {
    setActiveTab('invoices');
    fetchInvoices(false, { silent: true });
    if (!inv) return;
    setDocModalType('invoice'); setEditingDoc(inv); setShowDocModal(true);
  };
  const handleProofsChange = (poId, proofs) => {
    setPurchaseOrders(prev => prev.map(x => x._id === poId ? { ...x, payment_proofs: proofs } : x));
    setProofPO(prev => (prev && prev._id === poId ? { ...prev, payment_proofs: proofs } : prev));
  };
  const resetPOFilters = () => setPoFilters({
    status: 'all', approval_status: 'all', category: 'all',
    vendor: '', dateFrom: '', dateTo: '', search: ''
  });
  const hasActivePOFilters = poFilters.status !== 'all' ||
    poFilters.approval_status !== 'all' ||
    poFilters.category !== 'all' ||
    poFilters.vendor ||
    poFilters.dateFrom || poFilters.dateTo ||
    poFilters.search;
  
  // PDF Preview state
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewInvoiceNumber, setPreviewInvoiceNumber] = useState('');
  const [previewInvoiceId, setPreviewInvoiceId] = useState(null);
  const [previewClientName, setPreviewClientName] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Invoice metrics — always from backend (computed across ALL matching invoices)
  const invoiceMetrics = useMemo(() => {
    if (invoiceServerMetrics) {
      return {
        grossRetainerTotal: invoiceServerMetrics.gross_retainer_total || 0,
        grossNonRetainerTotal: invoiceServerMetrics.gross_non_retainer_total || 0,
        totalPaid: invoiceServerMetrics.total_paid || 0,
        totalUnpaid: invoiceServerMetrics.total_unpaid || 0,
        invoiceCount: invoiceTotal
      };
    }
    return { grossRetainerTotal: 0, grossNonRetainerTotal: 0, totalPaid: 0, totalUnpaid: 0, invoiceCount: 0 };
  }, [invoiceServerMetrics, invoiceTotal]);

  // Server handles filtering and sorting — invoices are already filtered/sorted
  const filteredAndSortedInvoices = invoices;

  // Handle invoice sort — triggers refetch via dependency
  const handleInvoiceSort = (field) => {
    if (invoiceSortField === field) {
      setInvoiceSortDir(invoiceSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setInvoiceSortField(field);
      setInvoiceSortDir('desc');
    }
  };

  // Handle date preset change
  const handleDatePresetChange = (preset) => {
    setInvoiceDatePreset(preset);
    if (preset === 'custom') return; // user will set dates manually
    const range = getDatePresetRange(preset);
    setInvoiceFilters(prev => ({ ...prev, dateFrom: range.from, dateTo: range.to }));
  };

  // Infinite scroll — auto-load more when scrolling to bottom
  const loadMoreRef = useRef(null);
  useEffect(() => {
    const checkNearBottom = () => {
      // Check the document/window scroll position
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      // Trigger when within 400px of the bottom
      return scrollHeight - scrollTop - clientHeight < 400;
    };

    const handleScroll = () => {
      if (checkNearBottom() && invoiceHasMore && !loadingMore) {
        setInvoicePage(prev => prev + 1);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [invoiceHasMore, loadingMore]);

  // Reset invoice filters
  const resetInvoiceFilters = () => {
    const defaultRange = getDatePresetRange('current_fy');
    setInvoiceDatePreset('current_fy');
    setInvoiceFilters({
      status: 'all',
      type: 'all',
      client: 'all',
      dateFrom: defaultRange.from,
      dateTo: defaultRange.to,
      search: ''
    });
    setInvoicePage(1);
    setClientFilterSearch('');
    setClientDropdownOpen(false);
  };

  // Update filters (page reset handled by the filter change effect)
  const updateInvoiceFilter = (key, value) => {
    if (key === 'dateFrom' || key === 'dateTo') {
      setInvoiceDatePreset('custom');
    }
    setInvoiceFilters(prev => ({ ...prev, [key]: value }));
  };

  // Check if any invoice filters are active (beyond the default FY dates)
  const hasActiveInvoiceFilters = invoiceFilters.status !== 'all' || 
    invoiceFilters.type !== 'all' || 
    invoiceFilters.client !== 'all' || 
    invoiceDatePreset !== 'current_fy' ||
    invoiceFilters.search;

  // Fetch dashboard data
  const fetchDashboard = useCallback(async ({ silent = false } = {}) => {
    // Skip dashboard fetch if user doesn't have dashboard permission
    if (!permissions.canViewDashboard) {
      return;
    }
    try {
      const res = await api.get(`${apiPrefix}/accounting/dashboard`);
      setDashboardData(res.data);
      if (!silent) setAccessDenied(false);
    } catch (error) {
      if (error.response?.status === 403) {
        // Only set access denied if user has NO permissions at all
        // If they have PO access, don't show access denied
        if (!permissions.canViewPOs) {
          if (!silent) setAccessDenied(true);
        }
      } else {
        console.error('Error fetching dashboard:', error);
        if (!silent) toast.error('Failed to load accounting data');
      }
    }
  }, [api, apiPrefix, permissions.canViewDashboard, permissions.canViewPOs]);

  // Fetch clients (for dropdowns and retainers tab)
  const fetchClients = useCallback(async () => {
    try {
      const clientsEndpoint = mode === 'admin' ? '/api/admin/clients' : '/api/employee/clients';
      const res = await api.get(clientsEndpoint);
      const activeClients = res.data || [];
      setClients(activeClients);
      
      // Build combined client list for filter (active + archived)
      if (mode === 'admin') {
        try {
          const archivedRes = await api.get('/api/admin/clients/archived');
          const archivedClients = (archivedRes.data?.clients || []).map(c => ({
            _id: c.id,
            name: c.company_name || c.name,
            company_state: c.company_state,
            is_archived: true
          }));
          const combined = [
            ...activeClients.map(c => ({ _id: c._id, name: c.company_name || c.name, company_state: c.company_state, is_archived: false })),
            ...archivedClients
          ];
          setAllClientsForFilter(combined);
        } catch {
          setAllClientsForFilter(activeClients.map(c => ({ _id: c._id, name: c.company_name || c.name, company_state: c.company_state, is_archived: false })));
        }
      } else {
        setAllClientsForFilter(activeClients.map(c => ({ _id: c._id, name: c.company_name || c.name, company_state: c.company_state, is_archived: false })));
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, [api, mode]);

  // Fetch clients with retainer data (for Retainers tab - Admin only)
  const [retainerClients, setRetainerClients] = useState([]);
  const fetchRetainerClients = useCallback(async () => {
    if (mode === 'admin') {
      // Admin already has full client data
      return;
    }
    if (!permissions.canViewRetainers) return;
    try {
      const res = await api.get('/api/employee/accounting/retainers');
      setRetainerClients(res.data || []);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching retainer clients:', error);
      }
    }
  }, [api, mode, permissions.canViewRetainers]);

  // Fetch invoices — server-side pagination with infinite scroll accumulation
  const fetchInvoices = useCallback(async (append = false, { silent = false } = {}) => {
    if (!permissions.canViewInvoices) return;
    try {
      const page = append ? invoicePage : 1;
      const params = {
        page,
        limit: invoiceLimit,
        sort_field: invoiceSortField,
        sort_dir: invoiceSortDir,
      };
      if (invoiceFilters.status !== 'all') params.status = invoiceFilters.status;
      if (invoiceFilters.type !== 'all') params.invoice_type = invoiceFilters.type;
      if (invoiceFilters.client !== 'all') params.client_id = invoiceFilters.client;
      if (invoiceFilters.dateFrom) params.date_from = invoiceFilters.dateFrom;
      if (invoiceFilters.dateTo) params.date_to = invoiceFilters.dateTo;
      if (invoiceFilters.search) params.search = invoiceFilters.search;
      
      const res = await api.get(`${apiPrefix}/accounting/invoices`, { params });
      const data = res.data;
      if (data.invoices) {
        if (append) {
          setInvoices(prev => [...prev, ...data.invoices]);
        } else {
          setInvoices(data.invoices);
        }
        setInvoiceTotal(data.total || 0);
        setInvoiceTotalPages(data.total_pages || 1);
        setInvoiceHasMore(page < (data.total_pages || 1));
        if (data.metrics) {
          setInvoiceServerMetrics(data.metrics);
        }
      } else {
        setInvoices(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching invoices:', error);
      }
    }
  }, [api, apiPrefix, permissions.canViewInvoices, invoicePage, invoiceLimit, invoiceSortField, invoiceSortDir, invoiceFilters]);

  // Fetch estimates
  const fetchEstimates = useCallback(async ({ silent = false } = {}) => {
    if (!permissions.canViewEstimates) return;
    try {
      const res = await api.get(`${apiPrefix}/accounting/estimates`);
      setEstimates(res.data || []);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching estimates:', error);
      }
    }
  }, [api, apiPrefix, permissions.canViewEstimates]);

  // Fetch purchase orders
  const fetchPurchaseOrders = useCallback(async ({ silent = false } = {}) => {
    if (!permissions.canViewPOs) return;
    try {
      const res = await api.get(`${apiPrefix}/accounting/purchase-orders`);
      setPurchaseOrders(res.data || []);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching POs:', error);
      }
    }
  }, [api, apiPrefix, permissions.canViewPOs]);

  // Fetch PO categories (mandatory picker in PO form)
  const fetchPOCategories = useCallback(async () => {
    if (!permissions.canViewPOs) return;
    try {
      const res = await api.get(`${apiPrefix}/accounting/po-categories`);
      setPoCategories(res.data || []);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching PO categories:', error);
      }
    }
  }, [api, apiPrefix, permissions.canViewPOs]);

  // Fetch vendors master (used by the vendor picker in the PO form)
  const fetchVendors = useCallback(async () => {
    if (!permissions.canViewPOs) return;
    try {
      const res = await api.get('/api/admin/accounting/vendors');
      const rows = res.data || [];
      setVendorSuggestions(rows);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching vendors:', error);
      }
    }
  }, [api, permissions.canViewPOs]);

  // Create new PO category on demand from the PO form
  const handleCreatePOCategory = useCallback(async (name) => {
    try {
      const res = await api.post(`${apiPrefix}/accounting/po-categories`, { name });
      toast.success(`Category "${res.data?.name}" added`);
      setPoCategories(prev => {
        const exists = prev.some(c => c._id === res.data?._id || (c.name || '').toLowerCase() === (res.data?.name || '').toLowerCase());
        return exists ? prev : [...prev, res.data].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      });
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add category');
      throw e;
    }
  }, [api, apiPrefix]);

  // Fetch salaries
  const fetchSalaries = useCallback(async ({ silent = false } = {}) => {
    if (!permissions.canViewSalaries) return;
    try {
      const res = await api.get(`${apiPrefix}/accounting/salaries`);
      setSalaries(res.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Error fetching salaries:', error);
      }
    }
  }, [api, apiPrefix, permissions.canViewSalaries]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchClients(),
        fetchRetainerClients(),
        fetchInvoices(),
        fetchEstimates(),
        fetchPurchaseOrders(),
        fetchPOCategories(),
        fetchVendors(),
        fetchSalaries()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchClients, fetchRetainerClients, fetchEstimates, fetchPurchaseOrders, fetchPOCategories, fetchVendors, fetchSalaries]);

  // Separate effect for invoice refetch on filter/sort changes — reset to page 1
  useEffect(() => {
    setInvoicePage(1);
    fetchInvoices(false);
  }, [invoiceFilters, invoiceSortField, invoiceSortDir]);

  // Load more effect — when page > 1, append
  useEffect(() => {
    if (invoicePage > 1) {
      setLoadingMore(true);
      fetchInvoices(true).finally(() => setLoadingMore(false));
    }
  }, [invoicePage]);

  // Fetch audit trail
  const fetchAuditTrail = useCallback(async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({ page: auditPage, limit: 50 });
      if (auditFilters.action) params.append('action', auditFilters.action);
      if (auditFilters.entity_type) params.append('entity_type', auditFilters.entity_type);
      if (auditFilters.user_email) params.append('user_email', auditFilters.user_email);
      if (auditFilters.date_from) params.append('date_from', auditFilters.date_from);
      if (auditFilters.date_to) params.append('date_to', auditFilters.date_to);
      const res = await api.get(`${apiPrefix}/accounting/audit-trail?${params.toString()}`);
      setAuditLogs(res.data.logs || []);
      setAuditTotal(res.data.total || 0);
      setAuditSummary(res.data.summary || null);
    } catch (error) {
      toast.error('Failed to load audit trail');
    } finally {
      setAuditLoading(false);
    }
  }, [api, apiPrefix, auditPage, auditFilters]);

  // Fetch audit trail when tab is active
  useEffect(() => {
    if (activeTab === 'audit_trail') {
      fetchAuditTrail();
    }
  }, [activeTab, fetchAuditTrail]);



  // Save document handler
  const handleSaveDocument = async (data) => {
    if (!permissions.canCreateInvoices && !permissions.canCreateEstimates && !permissions.canCreatePOs &&
        !permissions.canEditInvoices && !permissions.canEditEstimates && !permissions.canEditPOs) {
      toast.error('You do not have permission to save documents');
      return;
    }
    
    try {
      const endpoints = {
        invoice: `${apiPrefix}/accounting/invoices`,
        estimate: `${apiPrefix}/accounting/estimates`,
        purchase_order: `${apiPrefix}/accounting/purchase-orders`
      };

      let createdPO = null;
      let createdInvoiceForFollowUp = null;
      if (editingDoc) {
        const res = await api.put(`${endpoints[docModalType]}/${editingDoc._id}`, data);
        toast.success(`${docModalType.replace('_', ' ')} updated`);
        // Optimistic merge — patch the edited row in place instead of
        // reloading the whole collection, so the table doesn't flash.
        const merged = { ...editingDoc, ...data, ...(res?.data || {}), _id: editingDoc._id };
        if (docModalType === 'invoice') setInvoices(prev => prev.map(x => x._id === editingDoc._id ? merged : x));
        else if (docModalType === 'estimate') setEstimates(prev => prev.map(x => x._id === editingDoc._id ? merged : x));
        else setPurchaseOrders(prev => prev.map(x => x._id === editingDoc._id ? merged : x));
      } else {
        const res = await api.post(endpoints[docModalType], data);
        toast.success(`${docModalType.replace('_', ' ')} created`);
        const created = res?.data;
        if (created?._id) {
          if (docModalType === 'invoice') setInvoices(prev => [created, ...prev]);
          else if (docModalType === 'estimate') setEstimates(prev => [created, ...prev]);
          else setPurchaseOrders(prev => [created, ...prev]);
        } else {
          // Fallback silently — server didn't return the new doc
          if (docModalType === 'invoice') fetchInvoices(false, { silent: true });
          else if (docModalType === 'estimate') fetchEstimates({ silent: true });
          else fetchPurchaseOrders({ silent: true });
        }
        if (docModalType === 'purchase_order') createdPO = created;
        if (docModalType === 'invoice' && created?._id) createdInvoiceForFollowUp = created;
      }

      // Dashboard KPIs only need to refresh when the dashboard is actually
      // on screen; otherwise skip the extra network hit.
      if (['financial_dashboard', 'dashboard_overview'].includes(activeTab)) {
        fetchDashboard({ silent: true });
      }

      // Right after a NEW invoice is created, offer to deliver it to the
      // client on WhatsApp — mirrors the email post-create flow. Client's
      // registered mobile number (phone / mobile / whatsapp_phone) is
      // resolved by the backend; if it's missing, the toast tells the user.
      if (createdInvoiceForFollowUp) {
        setTimeout(() => {
          toast('Send this invoice on WhatsApp now?', {
            description: `${createdInvoiceForFollowUp.client_name || 'The client'} · ${createdInvoiceForFollowUp.invoice_number || ''}`,
            duration: 8000,
            action: {
              label: 'Send via WhatsApp',
              onClick: () => handleSendInvoiceWhatsApp(createdInvoiceForFollowUp),
            },
            cancel: { label: 'Skip', onClick: () => {} },
          });
        }, 400);
      }

      // Policy: every new PO must be submitted for approval immediately.
      // Auto-open the Submit-for-Approval modal so the accountant can't skip.
      if (createdPO && createdPO._id) {
        setSubmitPO(createdPO);
        toast.info('Every PO needs approval — pick approvers to send it out.', { duration: 6000 });
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || `Failed to save ${docModalType.replace('_', ' ')}`);
      throw error;
    }
  };

  // Delete document handler
  const handleDeleteDocument = async (type, id) => {
    // Check specific delete permissions
    const deletePermMap = {
      invoice: permissions.canDeleteInvoices,
      estimate: permissions.canDeleteEstimates,
      purchase_order: permissions.canDeletePOs
    };
    
    if (!deletePermMap[type]) {
      toast.error(`You do not have permission to delete ${type.replace('_', ' ')}s`);
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const endpoints = {
        invoice: `${apiPrefix}/accounting/invoices`,
        estimate: `${apiPrefix}/accounting/estimates`,
        purchase_order: `${apiPrefix}/accounting/purchase-orders`
      };

      const collectionSetter = {
        invoice: setInvoices,
        estimate: setEstimates,
        purchase_order: setPurchaseOrders,
      }[type];

      // Optimistic UI: drop the row locally first so the table doesn't flash.
      collectionSetter?.((prev) => Array.isArray(prev) ? prev.filter((d) => d._id !== id) : prev);

      await api.delete(`${endpoints[type]}/${id}`);
      toast.success('Document deleted');

      // Dashboard KPIs only need to refresh when the user is looking at them.
      // If they're on a sub-tab, we skip the extra request; the dashboard will
      // re-fetch fresh data the moment they navigate back.
      if (['financial_dashboard', 'dashboard_overview'].includes(activeTab)) {
        fetchDashboard({ silent: true });
      }
    } catch (error) {
      toast.error('Failed to delete document');
      // Rollback optimistic removal by re-fetching the list.
      if (type === 'invoice') fetchInvoices(false, { silent: true });
      else if (type === 'estimate') fetchEstimates({ silent: true });
      else fetchPurchaseOrders({ silent: true });
    }
  };

  // Create draft invoice from an existing PO — user then edits amounts
  const handleCreateInvoiceFromPO = async (poId, poNumber) => {
    if (!permissions.canCreateInvoices) {
      toast.error('You do not have permission to create invoices');
      return;
    }
    try {
      const res = await api.post(`${apiPrefix}/accounting/purchase-orders/${poId}/create-invoice`);
      toast.success(`Draft invoice ${res.data?.invoice_number || ''} created from ${poNumber}`);
      fetchInvoices(false, { silent: true });
      fetchDashboard({ silent: true });
      // Switch to invoices tab so user can edit the new draft
      setActiveTab('invoices');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice from PO');
    }
  };

  // Save retainer handler
  const handleSaveRetainer = async (clientId, data) => {
    if (!permissions.canEditRetainers) {
      toast.error('You do not have permission to edit retainers');
      return;
    }
    
    try {
      await api.put(`/api/admin/clients/${clientId}/retainer`, data);
      toast.success('Retainer updated');
      fetchDashboard({ silent: true });
      fetchClients();
    } catch (error) {
      toast.error('Failed to update retainer');
      throw error;
    }
  };

  // Void invoice handler
  const handleVoidInvoice = async (invoiceId, invoiceNumber) => {
    if (!window.confirm(`Are you sure you want to void invoice ${invoiceNumber}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await api.put(`${apiPrefix}/accounting/invoices/${invoiceId}/void`);
      toast.success('Invoice voided successfully');
      fetchInvoices(false, { silent: true });
      fetchDashboard({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void invoice');
    }
  };

  // Unvoid invoice handler — restore to previous status
  const handleUnvoidInvoice = async (invoiceId, invoiceNumber, previousStatus) => {
    const restoreTo = previousStatus && previousStatus !== 'voided' && previousStatus !== 'cancelled' ? previousStatus : 'draft';
    if (!window.confirm(`Restore invoice ${invoiceNumber}? It will return to "${restoreTo.replace('_', ' ')}" status.`)) {
      return;
    }
    
    try {
      const res = await api.put(`${apiPrefix}/accounting/invoices/${invoiceId}/unvoid`);
      toast.success(res.data?.message || `Invoice ${invoiceNumber} restored`);
      fetchInvoices(false, { silent: true });
      fetchDashboard({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore invoice');
    }
  };

  // Update invoice payment status
  const handleUpdateInvoiceStatus = async (invoiceId, newStatus, invoiceNumber) => {
    try {
      await api.put(`${apiPrefix}/accounting/invoices/${invoiceId}/status`, { status: newStatus });
      toast.success(`Invoice ${invoiceNumber} marked as ${newStatus.replace('_', ' ')}`);
      fetchInvoices(false, { silent: true });
      fetchDashboard({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice status');
    }
  };

  const handleUpdateFilingStatus = async (invoiceId, field, value) => {
    try {
      const payload = {};
      payload[field] = value;
      await api.patch(`${apiPrefix}/accounting/invoices/${invoiceId}/filing-status`, payload);
      setInvoices(prev => prev.map(inv => inv._id === invoiceId ? { ...inv, [field]: value } : inv));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update filing status');
    }
  };

  // Send invoice via WhatsApp (AiSensy) — auto-uses client's registered mobile.
  // No prompt: the backend resolves phone from the client's stored
  // phone / mobile / whatsapp_phone fields. If missing, we surface a clear
  // error so the user knows to add a number on the client record first.
  const handleSendInvoiceWhatsApp = async (inv) => {
    if (!inv?._id) return;
    const proceed = window.confirm(
      `Send invoice ${inv.invoice_number || ''} on WhatsApp to ${inv.client_name || 'the client'}'s registered mobile number?`
    );
    if (!proceed) return;
    try {
      const res = await api.post('/api/admin/whatsapp/send-invoice', {
        invoice_id: inv._id,
      });
      toast.success(`WhatsApp queued to ${res.data?.to || 'client'}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'WhatsApp send failed';
      if (/no valid phone/i.test(msg)) {
        toast.error(`${inv.client_name || 'This client'} has no WhatsApp number on record. Add one on the client's profile first.`);
      } else {
        toast.error(msg);
      }
    }
  };

  // Multi-select toggle handler
  const handleToggleInvoiceSelect = (invoiceId) => {
    setSelectedInvoiceIds(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  // Select all invoices handler
  const handleSelectAllInvoices = () => {
    if (selectedInvoiceIds.length === filteredAndSortedInvoices.length) {
      setSelectedInvoiceIds([]);
    } else {
      setSelectedInvoiceIds(filteredAndSortedInvoices.map(inv => inv._id));
    }
  };

  // Download multiple invoices as single PDF
  const handleDownloadMultipleInvoices = async () => {
    if (selectedInvoiceIds.length === 0) {
      toast.error('Please select at least one invoice');
      return;
    }
    
    try {
      toast.info(`Generating PDF for ${selectedInvoiceIds.length} invoices...`);
      // Add timestamp to bust cache
      const timestamp = new Date().getTime();
      const response = await api.post(`${apiPrefix}/accounting/invoices/download-multiple?t=${timestamp}`, {
        invoice_ids: selectedInvoiceIds
      }, {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoices_Batch_${getTodayLocal()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
      setSelectedInvoiceIds([]);
    } catch (error) {
      console.error('Multi-PDF download error:', error);
      toast.error('Failed to download PDFs');
    }
  };

  // Bulk delete multiple invoices
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  const handleBulkDeleteInvoices = async () => {
    if (selectedInvoiceIds.length === 0) {
      toast.error('Please select at least one invoice');
      return;
    }
    
    try {
      toast.info(`Deleting ${selectedInvoiceIds.length} invoices...`);
      const response = await api.post(`${apiPrefix}/accounting/invoices/bulk-delete`, {
        invoice_ids: selectedInvoiceIds
      });
      
      toast.success(response.data.message || `Deleted ${selectedInvoiceIds.length} invoices`);
      setSelectedInvoiceIds([]);
      setShowBulkDeleteConfirm(false);
      fetchInvoices(false, { silent: true });
    } catch (error) {
      console.error('Bulk delete error:', error);
      if (error.response?.status === 403) {
        toast.error('Only super admins can delete invoices');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete invoices');
      }
    }
  };

  // Clone single invoice
  const handleCloneInvoice = async (invoiceId) => {
    try {
      toast.info('Cloning invoice...');
      const response = await api.post(`${apiPrefix}/accounting/invoices/${invoiceId}/clone`);
      toast.success(`Invoice cloned as ${response.data.new_invoice.invoice_number}`);
      fetchInvoices(false, { silent: true });
    } catch (error) {
      console.error('Clone error:', error);
      if (error.response?.status === 403) {
        toast.error('Only super admins can clone invoices');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to clone invoice');
      }
    }
  };

  // Bulk clone multiple invoices
  const [showBulkCloneConfirm, setShowBulkCloneConfirm] = useState(false);
  
  const handleBulkCloneInvoices = async () => {
    if (selectedInvoiceIds.length === 0) {
      toast.error('Please select at least one invoice');
      return;
    }
    
    try {
      toast.info(`Cloning ${selectedInvoiceIds.length} invoices...`);
      const response = await api.post(`${apiPrefix}/accounting/invoices/bulk-clone`, {
        invoice_ids: selectedInvoiceIds
      });
      
      toast.success(response.data.message || `Cloned ${selectedInvoiceIds.length} invoices`);
      setSelectedInvoiceIds([]);
      setShowBulkCloneConfirm(false);
      fetchInvoices(false, { silent: true });
    } catch (error) {
      console.error('Bulk clone error:', error);
      if (error.response?.status === 403) {
        toast.error('Only super admins can clone invoices');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to clone invoices');
      }
    }
  };

  // Download PDF handler
  // Download estimate PDF (uses branded invoice PDF template in Estimate mode)
  const handleDownloadEstimatePDF = async (estimateId, estimateNumber, clientName) => {
    try {
      toast.info('Generating estimate PDF...');
      const timestamp = new Date().getTime();
      const response = await api.get(`${apiPrefix}/accounting/estimates/${estimateId}/pdf?t=${timestamp}`, {
        responseType: 'blob',
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', Pragma: 'no-cache', Expires: '0' }
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeClientName = (clientName || 'Client').replace(/[^a-zA-Z0-9\s-_]/g, '').trim().replace(/\s+/g, '_');
      link.download = `Estimate_${safeClientName}_${estimateNumber || estimateId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Estimate PDF downloaded');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to download estimate PDF');
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber, clientName) => {
    try {
      toast.info('Generating PDF...');
      // Add timestamp to bust cache and ensure fresh PDF generation
      const timestamp = new Date().getTime();
      const response = await api.get(`${apiPrefix}/accounting/invoices/${invoiceId}/pdf?t=${timestamp}`, {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Use client name + invoice number for filename
      const safeClientName = (clientName || 'Client').replace(/[^a-zA-Z0-9\s-_]/g, '').trim().replace(/\s+/g, '_');
      link.download = `${safeClientName}_${invoiceNumber || invoiceId}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  // Preview PDF handler - opens PDF in modal
  const handlePreviewPDF = async (invoiceId, invoiceNumber, clientName) => {
    try {
      setLoadingPreview(true);
      setPreviewInvoiceNumber(invoiceNumber || 'Invoice');
      setPreviewInvoiceId(invoiceId);
      setPreviewClientName(clientName || 'Invoice');
      setShowPdfPreview(true);
      
      // Add timestamp to bust cache and ensure fresh PDF generation
      const timestamp = new Date().getTime();
      const response = await api.get(`${apiPrefix}/accounting/invoices/${invoiceId}/pdf?t=${timestamp}`, {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      // Create blob URL for preview
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
      setLoadingPreview(false);
    } catch (error) {
      console.error('PDF preview error:', error);
      toast.error('Failed to load PDF preview');
      setShowPdfPreview(false);
      setLoadingPreview(false);
    }
  };

  // Close PDF preview and clean up
  const handleClosePdfPreview = () => {
    if (previewPdfUrl) {
      window.URL.revokeObjectURL(previewPdfUrl);
    }
    setPreviewPdfUrl(null);
    setShowPdfPreview(false);
    setPreviewInvoiceNumber('');
    setPreviewInvoiceId(null);
    setPreviewClientName('');
  };

  // Download from preview modal
  const handleDownloadFromPreview = () => {
    if (!previewPdfUrl) return;
    
    const link = document.createElement('a');
    link.href = previewPdfUrl;
    const safeClientName = (previewClientName || 'Invoice').replace(/[^a-zA-Z0-9]/g, '_');
    link.download = `${safeClientName}_${previewInvoiceNumber}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('PDF downloaded successfully');
  };

  // Download PO PDF for Customer (without costing data)
  const handleDownloadPOForCustomer = async (poId, poNumber) => {
    try {
      toast.info('Generating customer PDF...');
      const response = await api.get(`${apiPrefix}/accounting/purchase-orders/${poId}/download`, {
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${poNumber || 'PO'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Customer PDF downloaded successfully');
    } catch (error) {
      console.error('PO PDF download error:', error);
      toast.error('Failed to download customer PDF');
    }
  };

  // Generate recurring invoice handler
  const handleGenerateRecurringInvoice = async (invoiceId) => {
    if (!permissions.canCreateInvoices) {
      toast.error('You do not have permission to generate invoices');
      return;
    }
    
    try {
      toast.info('Generating next recurring invoice...');
      const response = await api.post(`${apiPrefix}/accounting/invoices/${invoiceId}/generate-recurring`);
      toast.success(`New invoice ${response.data.invoice_number} generated successfully`);
      fetchInvoices(false, { silent: true });
      fetchDashboard({ silent: true });
    } catch (error) {
      console.error('Generate recurring error:', error);
      const message = error.response?.data?.detail || 'Failed to generate recurring invoice';
      toast.error(message);
    }
  };

  // Invoice number validation - HED<YY>XXXXX format
  const validateInvoiceNumberFormat = (number) => {
    const pattern = /^HED\d{2}\d{5}$/;
    return pattern.test(number);
  };

  // Start editing invoice number
  const handleStartEditInvoiceNumber = (invoice) => {
    if (!permissions.canEdit) return;
    setEditingInvoiceId(invoice._id);
    setEditingInvoiceNumber(invoice.invoice_number);
    setInvoiceNumberError(null);
  };

  // Cancel editing invoice number
  const handleCancelEditInvoiceNumber = () => {
    setEditingInvoiceId(null);
    setEditingInvoiceNumber('');
    setInvoiceNumberError(null);
  };

  // ============ INLINE DATE EDITING ============
  const [editingDateInvoiceId, setEditingDateInvoiceId] = useState(null);
  const [editingInvoiceDate, setEditingInvoiceDate] = useState('');
  const [editingDueDate, setEditingDueDate] = useState('');
  const [savingDates, setSavingDates] = useState(false);

  // Start editing dates inline
  const handleStartEditDates = (invoice) => {
    if (!permissions.canEditInvoices) return;
    setEditingDateInvoiceId(invoice._id);
    setEditingInvoiceDate(invoice.invoice_date || '');
    setEditingDueDate(invoice.due_date || '');
  };

  // Cancel editing dates
  const handleCancelEditDates = () => {
    setEditingDateInvoiceId(null);
    setEditingInvoiceDate('');
    setEditingDueDate('');
  };

  // Save dates inline
  const handleSaveDates = async (invoiceId) => {
    if (!permissions.canEditInvoices) {
      toast.error('You do not have permission to edit invoices');
      return;
    }
    
    setSavingDates(true);
    try {
      await api.put(`${apiPrefix}/accounting/invoices/${invoiceId}/dates`, {
        invoice_date: editingInvoiceDate,
        due_date: editingDueDate
      });
      toast.success('Dates updated successfully');
      fetchInvoices(false, { silent: true });
      handleCancelEditDates();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update dates';
      toast.error(message);
    } finally {
      setSavingDates(false);
    }
  };

  // Save invoice number
  const handleSaveInvoiceNumber = async (invoiceId) => {
    if (!permissions.canEdit) {
      toast.error('You do not have permission to edit invoices');
      return;
    }
    
    if (!editingInvoiceNumber.trim()) {
      setInvoiceNumberError('Invoice number cannot be empty');
      return;
    }

    setSavingInvoiceNumber(true);
    try {
      const response = await api.put(`${apiPrefix}/accounting/invoices/${invoiceId}/number`, {
        invoice_number: editingInvoiceNumber.trim()
      });
      
      if (response.data.format_warning) {
        toast.warning(response.data.format_warning);
      } else {
        toast.success('Invoice number updated successfully');
      }
      
      // Refresh invoices
      fetchInvoices(false, { silent: true });
      handleCancelEditInvoiceNumber();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update invoice number';
      setInvoiceNumberError(message);
      toast.error(message);
    } finally {
      setSavingInvoiceNumber(false);
    }
  };

  // Migrate invoices to new format (admin only)
  const handleMigrateInvoices = async () => {
    if (mode !== 'admin') return;
    if (!window.confirm('This will migrate all existing invoices to the new HED format. Continue?')) return;
    
    try {
      toast.info('Migrating invoices...');
      const response = await api.post(`${apiPrefix}/accounting/invoices/migrate-numbers`);
      toast.success(response.data.message);
      fetchInvoices(false, { silent: true });
    } catch (error) {
      toast.error('Failed to migrate invoices');
    }
  };

  // Access denied view
  if (accessDenied) {
    return (
      <div className="p-6" data-testid="accounting-access-denied">
        <div className="max-w-lg mx-auto text-center py-16">
          <AlertCircle className="w-16 h-16 mx-auto text-red-400 mb-4" />
          <h2 className="text-2xl font-bold text-[#1a1a2e] mb-2">Access Denied</h2>
          <p className="text-neutral-600">
            {mode === 'admin' 
              ? 'Only Super Admin can access the Accounting module. This includes sensitive financial data such as client retainer values, employee salaries, and revenue metrics.'
              : 'You do not have permission to access the Accounting module. Please contact your administrator.'}
          </p>
        </div>
      </div>
    );
  }

  // Loading view
  if (loading) {
    // Skeleton — mirrors the post-load layout so there's no layout shift.
    return (
      <div className="p-6 space-y-6" data-testid="accounting-loading">
        {/* Header skeleton */}
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="h-7 w-48 rounded-md bg-neutral-200 animate-pulse" />
            <div className="h-3 w-80 rounded bg-neutral-100 animate-pulse" />
          </div>
          <div className="flex gap-2">
            <div className="h-9 w-24 rounded-xl bg-neutral-200 animate-pulse" />
            <div className="h-9 w-24 rounded-xl bg-neutral-200 animate-pulse" />
          </div>
        </div>
        {/* Tab bar skeleton */}
        <div className="flex gap-2 border-b border-neutral-200 pb-2 overflow-hidden">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="h-8 w-24 rounded-xl bg-neutral-100 animate-pulse flex-shrink-0" />
          ))}
        </div>
        {/* Metric cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="glass-card p-5 space-y-3">
              <div className="h-8 w-8 rounded-lg bg-neutral-200 animate-pulse" />
              <div className="h-3 w-24 rounded bg-neutral-200 animate-pulse" />
              <div className="h-7 w-32 rounded bg-neutral-300 animate-pulse" />
            </div>
          ))}
        </div>
        {/* Table skeleton */}
        <div className="glass-card p-5 space-y-2">
          <div className="h-4 w-32 rounded bg-neutral-200 animate-pulse mb-4" />
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex items-center gap-4 py-2.5">
              <div className="h-4 w-28 rounded bg-neutral-100 animate-pulse" />
              <div className="h-4 w-20 rounded bg-neutral-100 animate-pulse" />
              <div className="h-4 flex-1 rounded bg-neutral-100 animate-pulse" />
              <div className="h-4 w-20 rounded bg-neutral-100 animate-pulse" />
              <div className="h-4 w-16 rounded bg-neutral-100 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // GROUPED TAB ARCHITECTURE — 5 primary groups with sub-tabs
  // ═══════════════════════════════════════════════════════════
  const tabGroups = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: BarChart2,
      show: permissions.canViewDashboard,
      subtabs: [] // no sub-tabs — single unified view
    },
    {
      key: 'receivables',
      label: 'Receivables',
      icon: ArrowUpRight,
      show: permissions.canViewInvoices || permissions.canViewDashboard,
      subtabs: [
        { key: 'invoices', label: 'Invoices', show: permissions.canViewInvoices },
        { key: 'credit_notes', label: 'Credit Notes', show: permissions.canViewDashboard },
        { key: 'payments', label: 'Payments', show: permissions.canViewDashboard },
      ]
    },
    {
      key: 'payables',
      label: 'Payables',
      icon: ArrowDownRight,
      show: permissions.canViewPOs || permissions.canViewSalaries || permissions.canViewDashboard,
      subtabs: [
        { key: 'purchase_orders', label: 'Purchase Orders', show: permissions.canViewPOs },
        { key: 'po_analytics', label: 'PO Analytics', show: mode === 'admin' && permissions.canViewPOs },
        { key: 'vendors', label: 'Vendors', show: permissions.canViewPOs },
        { key: 'po_categories', label: 'PO Categories', show: permissions.canViewPOs },
        { key: 'expenses', label: 'Expenses', show: permissions.canViewDashboard },
        { key: 'salaries', label: 'Salaries', show: permissions.canViewSalaries },
      ]
    },
    {
      key: 'pipeline',
      label: 'Pipeline',
      icon: TrendingUp,
      show: permissions.canViewEstimates || permissions.canViewRetainers,
      subtabs: [
        { key: 'estimates', label: 'Estimates', show: permissions.canViewEstimates },
        { key: 'retainers', label: 'Retainers', show: permissions.canViewRetainers },
      ]
    },
    {
      key: 'agreements',
      label: 'Proposals & Contracts',
      icon: FileSignature,
      show: mode === 'admin' && permissions.canViewInvoices,
      subtabs: []
    },
    {
      key: 'reports',
      label: 'Reports & Compliance',
      icon: PieChart,
      show: permissions.canViewDashboard,
      subtabs: [
        { key: 'report_pnl', label: 'Profit & Loss', show: permissions.canViewDashboard },
        { key: 'report_balance_sheet', label: 'Balance Sheet', show: permissions.canViewDashboard },
        { key: 'report_revenue', label: 'Revenue', show: permissions.canViewDashboard },
        { key: 'report_aged_receivables', label: 'Aged Receivables', show: permissions.canViewDashboard },
        { key: 'report_po_analytics', label: 'PO Analytics', show: mode === 'admin' && permissions.canViewPOs },
        { key: 'report_client_statement', label: 'Client Statements', show: permissions.canViewDashboard },
        { key: 'gst_returns', label: 'GST Returns', show: permissions.canViewDashboard },
        { key: 'gst_tds_internal', label: 'GST & TDS Internal', show: permissions.canViewDashboard },
      ]
    },
    {
      key: 'audit_trail',
      label: 'Audit Trail',
      icon: Shield,
      show: permissions.canViewDashboard,
      subtabs: []
    },
  ];

  const visibleGroups = tabGroups.filter(g => g.show);

  // Active group is derived from activeTab. Note: `vendors` and
  // `po_categories` live under the Payables group so the sidebar keeps the
  // right pill highlighted while the user navigates PO sub-pages.
  const getActiveGroup = () => {
    // Dashboard group
    if (activeTab === 'financial_dashboard' || activeTab === 'dashboard_overview') return 'dashboard';
    // Receivables
    if (['invoices', 'credit_notes', 'payments'].includes(activeTab)) return 'receivables';
    // Payables (POs, sub-tools: Vendors + PO Categories, plus Expenses / Salaries)
    if (['purchase_orders', 'po_analytics', 'vendors', 'po_categories', 'expenses', 'salaries'].includes(activeTab)) return 'payables';
    // Pipeline
    if (['estimates', 'retainers'].includes(activeTab)) return 'pipeline';
    // Reports
    if (activeTab.startsWith('report_') || activeTab === 'gst_returns' || activeTab === 'gst_tds_internal') return 'reports';
    // Audit Trail
    if (activeTab === 'audit_trail') return 'audit_trail';
    if (activeTab === 'agreements') return 'agreements';
    return 'dashboard';
  };

  const activeGroup = getActiveGroup();

  const handleGroupClick = (group) => {
    if (group.subtabs.length === 0) {
      // No subtabs — set active tab to the group's own key
      if (group.key === 'dashboard') {
        setActiveTab('financial_dashboard');
      } else {
        setActiveTab(group.key);
      }
    } else {
      // Navigate to first visible sub-tab of the group
      const firstVisible = group.subtabs.find(s => s.show);
      if (firstVisible) setActiveTab(firstVisible.key);
    }
  };

  const currentGroupObj = visibleGroups.find(g => g.key === activeGroup);
  const visibleSubtabs = currentGroupObj?.subtabs?.filter(s => s.show) || [];

  // Legacy compatibility: map old tab keys
  const allTabs = visibleGroups.flatMap(g =>
    g.subtabs.length > 0 ? g.subtabs.filter(s => s.show) : [{ key: g.key === 'dashboard' ? 'financial_dashboard' : g.key, label: g.label, show: g.show }]
  );
  const tabs = allTabs;

  // Set default active tab to Dashboard
  const defaultTab = 'financial_dashboard';
  
  // Filter POs for regular employees (own POs only)
  const displayedPurchaseOrdersRaw = permissions.viewOwnPOsOnly && currentEmployeeId
    ? purchaseOrders.filter(po => po.created_by === currentEmployeeId || po.employee_id === currentEmployeeId)
    : purchaseOrders;

  // Apply user-facing PO filters on top of role-based scoping.
  // Plain computed constant — NOT a hook — so it stays outside the hooks
  // order-of-call contract even after early `if (loading) return` above.
  const poFilteredBase = (() => {
    if (!hasActivePOFilters) return displayedPurchaseOrdersRaw;
    const q = (poFilters.search || '').toLowerCase().trim();
    const vq = (poFilters.vendor || '').toLowerCase().trim();
    const df = poFilters.dateFrom ? new Date(poFilters.dateFrom) : null;
    const dt = poFilters.dateTo ? new Date(poFilters.dateTo + 'T23:59:59') : null;
    return displayedPurchaseOrdersRaw.filter(po => {
      if (poFilters.status !== 'all' && (po.status || 'draft') !== poFilters.status) return false;
      if (poFilters.approval_status !== 'all' && (po.approval_status || 'not_submitted') !== poFilters.approval_status) return false;
      if (poFilters.category !== 'all' && (po.category || '') !== poFilters.category) return false;
      if (vq && !((po.vendor_name || '').toLowerCase().includes(vq))) return false;
      if (q && !(
        (po.po_number || '').toLowerCase().includes(q) ||
        (po.vendor_name || '').toLowerCase().includes(q) ||
        (po.client_name || '').toLowerCase().includes(q) ||
        (po.notes || '').toLowerCase().includes(q)
      )) return false;
      if (df || dt) {
        const pd = po.po_date ? new Date(po.po_date) : (po.created_at ? new Date(po.created_at) : null);
        if (!pd) return false;
        if (df && pd < df) return false;
        if (dt && pd > dt) return false;
      }
      return true;
    });
  })();
  const displayedPurchaseOrders = (!poPaymentChip || poPaymentChip === 'all')
    ? poFilteredBase
    : poFilteredBase.filter(po => getPaymentState(po) === poPaymentChip);

  // Determine subtitle based on mode and role
  const getSubtitle = () => {
    if (mode === 'admin') return 'Financial management and reporting';
    // All employees get the same subtitle (Boss privileges eliminated)
    return 'Purchase Orders';
  };

  // Determine permission notice
  const getPermissionNotice = () => {
    if (mode === 'admin') return null;
    // All employees get the same permission notice (Boss privileges eliminated)
    return 'You can create and edit your own Purchase Orders only.';
  };

  return (
    <div className="p-3 sm:p-6" data-testid="accounting-page">
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1a1a2e]">Accounting</h1>
          <p className="text-neutral-500 text-xs sm:text-sm">{getSubtitle()}</p>
          {mode === 'employee' && (
            <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
              <Lock className="w-3 h-3" /> {getPermissionNotice()}
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={() => {
          fetchDashboard();
          fetchInvoices();
          fetchEstimates();
          fetchPurchaseOrders();
          fetchSalaries();
        }} className="w-full sm:w-auto">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* ═══ PRIMARY GROUP TABS ═══ */}
      <div className="mb-1" data-testid="accounting-tab-groups">
        <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-3 px-3 sm:mx-0 sm:px-0 scrollbar-hide">
          {visibleGroups.map(group => {
            const Icon = group.icon;
            const isActive = activeGroup === group.key;
            return (
              <button
                key={group.key}
                onClick={() => handleGroupClick(group)}
                data-testid={`group-${group.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap flex-shrink-0 border ${
                  isActive
                    ? 'bg-gray-900 text-white border-gray-900 shadow-soft'
                    : 'bg-white text-neutral-600 border-white/40 hover:bg-white/60 hover:border-white/40 hover:text-[#1a1a2e]'
                }`}
              >
                <Icon className="w-4 h-4" />
                {group.label}
                {group.subtabs.length > 0 && (
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isActive ? 'rotate-180 opacity-60' : 'opacity-40'}`} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ SUB-TABS (secondary row) ═══ */}
      {visibleSubtabs.length > 0 && (
        <div className="mb-5 sm:mb-6" data-testid="accounting-subtabs">
          <div className="flex gap-1 overflow-x-auto pb-1 -mx-3 px-3 sm:mx-0 sm:px-0 scrollbar-hide border-b border-white/40">
            {visibleSubtabs.map(subtab => (
              <button
                key={subtab.key}
                onClick={() => setActiveTab(subtab.key)}
                data-testid={`subtab-${subtab.key}`}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex-shrink-0 border-b-2 transition-colors ${
                  activeTab === subtab.key
                    ? 'border-pink-500 text-pink-600'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-white/40'
                }`}
              >
                {subtab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Spacer when dashboard (no subtabs) */}
      {visibleSubtabs.length === 0 && <div className="mb-4 sm:mb-6" />}

      {/* ═══ DASHBOARD (Unified: KPI Dashboard + Client Overview) ═══ */}
      {activeTab === 'financial_dashboard' && (
        <div className="space-y-6">
          {mode === 'admin' && permissions.canViewInvoices && (
            <RetainerGapPanel onOpenInvoice={openInvoiceInEditor} refreshKey={invoices.length} />
          )}
          {/* KPI Dashboard */}
          <AccountingDashboard onNavigate={(tab, param) => {
            // Map old report navigation to new report_ keys
            if (tab === 'reports' && param) {
              const reportMap = {
                'profit-loss': 'report_pnl',
                'balance-sheet': 'report_balance_sheet',
                'revenue': 'report_revenue',
                'aged-receivables': 'report_aged_receivables',
                'client-statement': 'report_client_statement',
              };
              setActiveTab(reportMap[param] || 'report_pnl');
            } else {
              setActiveTab(tab);
              if (param) {
                if (tab === 'invoices' && param === 'overdue') {
                  setInvoiceFilters(prev => ({ ...prev, status: 'overdue' })); setInvoicePage(1);
                } else if (tab === 'invoices' && param !== 'overdue') {
                  setSelectedInvoiceId(param);
                }
              }
            }
          }} />

          {/* Client Financial Overview */}
          {dashboardData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-[#1a1a2e]">Client Financial Breakdown</h2>
                <span className="text-xs text-neutral-400">Retainer clients: {dashboardData.metrics.clients_with_retainer} | Total retainer value: {formatCurrency(dashboardData.metrics.total_retainer_value)}</span>
              </div>
              <ClientBreakdownTable data={dashboardData.client_breakdown} totals={dashboardData.totals} />
            </div>
          )}
        </div>
      )}

      {/* ═══ REPORTS — Direct sub-tab routing ═══ */}
      {activeTab === 'report_pnl' && (
        <AccountingReports initialReport="profit-loss" onNavigate={(report) => setReportSubView(report)} />
      )}
      {activeTab === 'report_balance_sheet' && (
        <AccountingReports initialReport="balance-sheet" onNavigate={(report) => setReportSubView(report)} />
      )}
      {activeTab === 'report_revenue' && (
        <AccountingReports initialReport="revenue" onNavigate={(report) => setReportSubView(report)} />
      )}
      {activeTab === 'report_aged_receivables' && (
        <AccountingReports initialReport="aged-receivables" onNavigate={(report) => setReportSubView(report)} />
      )}
      {activeTab === 'report_client_statement' && (
        <AccountingReports initialReport="client-statement" onNavigate={(report) => setReportSubView(report)} />
      )}

      {(activeTab === 'po_analytics' || activeTab === 'report_po_analytics') && mode === 'admin' && permissions.canViewPOs && (
        <POAnalytics />
      )}

      {/* Credit Notes Tab */}
      {activeTab === 'credit_notes' && (
        <CreditNotes />
      )}

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <PaymentRecording />
      )}

      {/* GST Returns Tab */}
      {activeTab === 'gst_returns' && (
        <GSTReturns />
      )}

      {/* GST & TDS Internal Tracker Tab */}
      {activeTab === 'gst_tds_internal' && (
        <AccountingReports initialReport="gst-tds-internal" onNavigate={(report) => setReportSubView(report)} />
      )}

      {/* Audit Trail Tab */}
      {activeTab === 'agreements' && mode === 'admin' && permissions.canViewInvoices && <AgreementsHub />}

      {activeTab === 'audit_trail' && (
        <div className="space-y-4" data-testid="accounting-audit-trail">
          {/* Summary Stats */}
          {auditSummary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="glass-card p-4 rounded-xl">
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Total Logs</div>
                <div className="text-xl font-bold text-neutral-900 dark:text-white">{auditSummary.total_logs?.toLocaleString()}</div>
              </div>
              <div className="glass-card p-4 rounded-xl">
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Unique Users</div>
                <div className="text-xl font-bold text-neutral-900 dark:text-white">{auditSummary.unique_users}</div>
              </div>
              <div className="glass-card p-4 rounded-xl col-span-2">
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-1">Top Actions</div>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {Object.entries(auditSummary.actions_breakdown || {}).slice(0, 5).map(([action, count]) => (
                    <span key={action} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300">
                      {action.replace(/_/g, ' ')} <span className="font-semibold">{count}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="glass-card p-4 rounded-xl">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="text-xs text-neutral-500 dark:text-neutral-400 block mb-1">Action</label>
                <SearchableSelect
                  value={auditFilters.action}
                  onChange={(v) => { setAuditFilters(f => ({ ...f, action: v })); setAuditPage(1); }}
                  placeholder="All Actions"
                  triggerClassName="h-9 min-w-[200px] text-sm rounded-lg"
                  options={[
                    { value: '', label: 'All Actions' },
                    { value: 'create_invoice', label: 'Create Invoice' },
                    { value: 'update_invoice', label: 'Update Invoice' },
                    { value: 'delete_invoice', label: 'Delete Invoice' },
                    { value: 'update_invoice_status', label: 'Status Change' },
                    { value: 'void_invoice', label: 'Void Invoice' },
                    { value: 'create_purchase_order', label: 'Create PO' },
                    { value: 'update_purchase_order', label: 'Update PO' },
                    { value: 'create_estimate', label: 'Create Estimate' },
                    { value: 'update_estimate', label: 'Update Estimate' },
                    { value: 'update_employee_salary', label: 'Update Salary' },
                  ]}
                  data-testid="audit-filter-action"
                />
              </div>
              <div>
                <label className="text-xs text-neutral-500 dark:text-neutral-400 block mb-1">Entity Type</label>
                <SearchableSelect
                  value={auditFilters.entity_type}
                  onChange={(v) => { setAuditFilters(f => ({ ...f, entity_type: v })); setAuditPage(1); }}
                  placeholder="All Types"
                  triggerClassName="h-9 min-w-[180px] text-sm rounded-lg"
                  options={[
                    { value: '', label: 'All Types' },
                    { value: 'invoice', label: 'Invoices' },
                    { value: 'purchase_order', label: 'Purchase Orders' },
                    { value: 'estimate', label: 'Estimates' },
                    { value: 'expense', label: 'Expenses' },
                    { value: 'employee', label: 'Salary' },
                  ]}
                  data-testid="audit-filter-entity"
                />
              </div>
              <div>
                <label className="text-xs text-neutral-500 dark:text-neutral-400 block mb-1">User</label>
                <input type="text" placeholder="Email..." className="px-3 py-1.5 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 w-44" data-testid="audit-filter-user"
                  value={auditFilters.user_email} onChange={e => setAuditFilters(f => ({ ...f, user_email: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') { setAuditPage(1); fetchAuditTrail(); } }} />
              </div>
              <div>
                <label className="text-xs text-neutral-500 dark:text-neutral-400 block mb-1">From</label>
                <input type="date" className="px-3 py-1.5 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200" data-testid="audit-filter-from"
                  value={auditFilters.date_from} onChange={e => { setAuditFilters(f => ({ ...f, date_from: e.target.value })); setAuditPage(1); }} />
              </div>
              <div>
                <label className="text-xs text-neutral-500 dark:text-neutral-400 block mb-1">To</label>
                <input type="date" className="px-3 py-1.5 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200" data-testid="audit-filter-to"
                  value={auditFilters.date_to} onChange={e => { setAuditFilters(f => ({ ...f, date_to: e.target.value })); setAuditPage(1); }} />
              </div>
              <button onClick={() => { setAuditFilters({ action: '', entity_type: '', user_email: '', date_from: '', date_to: '' }); setAuditPage(1); }}
                className="px-3 py-1.5 text-sm rounded-lg bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors" data-testid="audit-clear-filters">
                Clear
              </button>
            </div>
          </div>

          {/* Audit Log List */}
          <div className="space-y-2">
            {auditLoading ? (
              <div className="glass-card p-8 rounded-xl text-center text-neutral-500 dark:text-neutral-400">Loading audit trail...</div>
            ) : auditLogs.length === 0 ? (
              <div className="glass-card p-8 rounded-xl text-center text-neutral-500 dark:text-neutral-400">No audit logs found for the selected filters.</div>
            ) : (
              auditLogs.map((log, idx) => {
                const isExpanded = expandedLogId === idx;
                const changes = log.details?.changes || [];
                const hasChanges = changes.length > 0;
                const actionLabel = (log.action || '').replace(/_/g, ' ');
                const timestamp = log.timestamp ? new Date(log.timestamp) : null;
                const entityLabel = (log.entity_type || '').replace(/_/g, ' ');
                const refNum = log.details?.ref_number || log.details?.invoice_number || log.details?.po_number || log.details?.estimate_number || '';

                return (
                  <div key={idx} className="glass-card rounded-xl overflow-hidden" data-testid={`audit-log-${idx}`}>
                    <button className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-neutral-50/50 dark:hover:bg-neutral-800/50 transition-colors"
                      onClick={() => setExpandedLogId(isExpanded ? null : idx)}>
                      {/* Action icon */}
                      <div className={`mt-0.5 p-1.5 rounded-lg shrink-0 ${
                        log.action?.includes('delete') ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
                        log.action?.includes('create') ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' :
                        log.action?.includes('void') ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' :
                        'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      }`}>
                        {log.action?.includes('delete') ? <Trash2 className="w-3.5 h-3.5" /> :
                         log.action?.includes('create') ? <Plus className="w-3.5 h-3.5" /> :
                         <Edit className="w-3.5 h-3.5" />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-neutral-900 dark:text-white capitalize">{actionLabel}</span>
                          {refNum && <span className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 font-mono">{refNum}</span>}
                          <span className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 capitalize">{entityLabel}</span>
                          {hasChanges && <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">{changes.length} change{changes.length > 1 ? 's' : ''}</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                          <User className="w-3 h-3" />
                          <span>{log.user_name || log.user_email || 'Unknown'}</span>
                          {log.user_role && log.user_role !== 'admin' && (
                            <span className="px-1 py-0.5 rounded bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-[10px] uppercase">{log.user_role}</span>
                          )}
                          <span className="text-neutral-300 dark:text-neutral-600">|</span>
                          <Clock className="w-3 h-3" />
                          <span>{timestamp ? timestamp.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}</span>
                        </div>
                      </div>

                      <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform mt-1 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-neutral-100 dark:border-neutral-800">
                        {hasChanges ? (
                          <div className="mt-3 space-y-1.5">
                            <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2">Field-Level Changes</div>
                            <div className="bg-neutral-50 dark:bg-neutral-900/50 rounded-lg overflow-hidden">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-neutral-200 dark:border-neutral-700">
                                    <th className="text-left px-3 py-2 text-neutral-500 dark:text-neutral-400 font-medium">Field</th>
                                    <th className="text-left px-3 py-2 text-neutral-500 dark:text-neutral-400 font-medium">Before</th>
                                    <th className="text-center px-1 py-2"></th>
                                    <th className="text-left px-3 py-2 text-neutral-500 dark:text-neutral-400 font-medium">After</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {changes.map((c, ci) => (
                                    <tr key={ci} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                                      <td className="px-3 py-2 font-mono text-neutral-700 dark:text-neutral-300">{c.field?.replace(/_/g, ' ')}</td>
                                      <td className="px-3 py-2 text-red-600 dark:text-red-400 max-w-[200px] truncate">{
                                        typeof c.old_value === 'object' ? JSON.stringify(c.old_value)?.substring(0, 80) : String(c.old_value ?? '—')
                                      }</td>
                                      <td className="px-1 py-2 text-center"><ArrowRight className="w-3 h-3 text-neutral-400 inline" /></td>
                                      <td className="px-3 py-2 text-emerald-600 dark:text-emerald-400 max-w-[200px] truncate">{
                                        typeof c.new_value === 'object' ? JSON.stringify(c.new_value)?.substring(0, 80) : String(c.new_value ?? '—')
                                      }</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
                            <div className="font-medium mb-1">Details</div>
                            <pre className="bg-neutral-50 dark:bg-neutral-900/50 rounded-lg p-3 overflow-x-auto text-[11px]">
                              {JSON.stringify(log.details || {}, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {auditTotal > 50 && (
            <div className="flex items-center justify-between text-sm text-neutral-500 dark:text-neutral-400">
              <span>Showing {((auditPage - 1) * 50) + 1}–{Math.min(auditPage * 50, auditTotal)} of {auditTotal}</span>
              <div className="flex gap-2">
                <button disabled={auditPage <= 1} onClick={() => setAuditPage(p => p - 1)}
                  className="px-3 py-1 rounded-lg bg-neutral-100 dark:bg-neutral-800 disabled:opacity-40 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors" data-testid="audit-prev-page">
                  Previous
                </button>
                <button disabled={auditPage * 50 >= auditTotal} onClick={() => setAuditPage(p => p + 1)}
                  className="px-3 py-1 rounded-lg bg-neutral-100 dark:bg-neutral-800 disabled:opacity-40 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors" data-testid="audit-next-page">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* (Overview merged into Dashboard above) */}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && permissions.canViewInvoices && (
        <div className="space-y-4">
          {mode === 'admin' && (
            <div className="flex justify-end">
              <RetainerGapChip onOpenInvoice={openInvoiceInEditor} refreshKey={`${invoices.length}:${invoices[0]?._id || ''}:${invoices[0]?.status || ''}`} />
            </div>
          )}
          {mode === 'admin' && (
            <CategoryReviewBanner
              refreshKey={`${invoices.length}:${invoices[0]?._id || ''}:${invoices[0]?.invoice_type || ''}`}
              onApplied={() => fetchInvoices(false, { silent: true })}
            />
          )}
          {/* Invoice Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <MetricCard
              title="Gross Retainer Total"
              value={formatCurrency(invoiceMetrics.grossRetainerTotal)}
              icon={Receipt}
              color="purple"
              subtext="No deductions"
            />
            <MetricCard
              title="Gross Non-Retainer"
              value={formatCurrency(invoiceMetrics.grossNonRetainerTotal)}
              icon={FileText}
              color="blue"
              subtext="No deductions"
            />
            <MetricCard
              title="Total Paid"
              value={formatCurrency(invoiceMetrics.totalPaid)}
              icon={CheckCircle}
              color="green"
              subtext="After TDS deduction"
            />
            <MetricCard
              title="Total Unpaid"
              value={formatCurrency(invoiceMetrics.totalUnpaid)}
              icon={Clock}
              color="amber"
              subtext="After TDS deduction"
            />
            <MetricCard
              title="Total Invoices"
              value={invoiceTotal.toString()}
              icon={FileText}
              color="gray"
              subtext={invoices.length < invoiceTotal ? `${invoices.length} loaded` : 'All loaded'}
            />
          </div>

          {/* Header with Filters */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-[#1a1a2e]">Invoices</h2>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowInvoiceFilters(!showInvoiceFilters)}
                className={hasActiveInvoiceFilters ? 'border-pink-500 text-pink-600' : ''}
              >
                <Filter className="w-4 h-4 mr-1" />
                Filters {hasActiveInvoiceFilters && `(${Object.values(invoiceFilters).filter(v => v && v !== 'all').length})`}
              </Button>
              {hasActiveInvoiceFilters && (
                <Button variant="ghost" size="sm" onClick={resetInvoiceFilters}>
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {selectedInvoiceIds.length > 0 && (
                <>
                  <Button variant="outline" size="sm" onClick={handleDownloadMultipleInvoices} data-testid="bulk-download-btn">
                    <Download className="w-4 h-4 mr-2" /> Download {selectedInvoiceIds.length} Selected
                  </Button>
                  {permissions.canCreateInvoices && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                      onClick={() => setShowBulkCloneConfirm(true)}
                      data-testid="bulk-clone-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" /> Clone {selectedInvoiceIds.length} Selected
                    </Button>
                  )}
                  {permissions.canDeleteInvoices && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      onClick={() => setShowBulkDeleteConfirm(true)}
                      data-testid="bulk-delete-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete {selectedInvoiceIds.length} Selected
                    </Button>
                  )}
                </>
              )}
              {permissions.canCreateInvoices && (
                <>
                  {mode === 'admin' && (
                    <>
                      <Button variant="outline" size="sm" onClick={handleMigrateInvoices} title="Migrate all invoices to HED format">
                        <RefreshCw className="w-4 h-4 mr-2" /> Migrate Numbers
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setShowZohoImport(true)} data-testid="import-invoices-btn">
                        <Upload className="w-4 h-4 mr-2" /> Import Invoices
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setShowZohoReportImport(true)} data-testid="import-zoho-report-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                        <Upload className="w-4 h-4 mr-2" /> Import from Zoho
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setShowImportHistory(true)} data-testid="import-history-btn" title="View past import batches and rollback">
                        <RefreshCw className="w-4 h-4 mr-2" /> Import History
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            const api = mode === 'admin' ? adminApi : employeeApi;
                            const res = await api.get('/api/admin/accounting/export/invoices', { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const a = document.createElement('a'); a.href = url; a.download = `invoices_export_${getTodayLocal()}.csv`; a.click();
                            window.URL.revokeObjectURL(url);
                          } catch { toast.error('Failed to export invoices'); }
                        }}
                        data-testid="export-invoices-btn"
                      >
                        <Download className="w-4 h-4 mr-2" /> Export CSV
                      </Button>
                    </>
                  )}
                  <Button size="sm" onClick={() => { setDocModalType('invoice'); setEditingDoc(null); setShowDocModal(true); }}>
                    <Plus className="w-4 h-4 mr-2" /> Create Invoice
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Filter Panel — bottom-sheet on mobile, inline on desktop */}
          {showInvoiceFilters && (
            <>
              {/* Mobile backdrop */}
              <div className="md:hidden fixed inset-0 bg-black/40 z-[70]" onClick={() => setShowInvoiceFilters(false)} data-testid="invoice-filter-backdrop" />
              <div
                className="max-md:fixed max-md:left-0 max-md:right-0 max-md:bottom-0 max-md:z-[71] max-md:bg-white max-md:rounded-t-2xl max-md:shadow-2xl max-md:max-h-[85vh] max-md:overflow-y-auto md:bg-white/40 md:rounded-xl md:border md:border-white/40 p-4"
                data-testid="invoice-filter-sheet"
              >
                {/* Mobile drag handle + header */}
                <div className="md:hidden -mx-4 -mt-4 pt-3 pb-2 border-b border-neutral-100 mb-3 sticky top-0 bg-white z-10">
                  <div className="flex justify-center mb-2">
                    <div className="h-1.5 w-10 rounded-full bg-neutral-300" />
                  </div>
                  <div className="px-4 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-neutral-900">Filter invoices</h3>
                    <button onClick={() => setShowInvoiceFilters(false)} className="p-1.5 rounded-lg hover:bg-neutral-100" data-testid="invoice-filter-close">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {/* Search */}
                <div className="col-span-2 sm:col-span-1">
                  <label className="text-xs font-medium text-neutral-600 mb-1 block">Search</label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-2 top-1/2 transform -translate-y-1/2 text-neutral-400" />
                    <Input
                      placeholder="Invoice # or client..."
                      value={invoiceFilters.search}
                      onChange={(e) => updateInvoiceFilter('search', e.target.value)}
                      className="pl-8 h-9"
                    />
                  </div>
                </div>
                
                {/* Status Filter */}
                <div>
                  <label className="text-xs font-medium text-neutral-600 mb-1 block">Status</label>
                  <Select value={invoiceFilters.status} onValueChange={(v) => updateInvoiceFilter('status', v)}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="partially_paid">Partially Paid</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                      <SelectItem value="voided">Voided</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Type Filter */}
                <div>
                  <label className="text-xs font-medium text-neutral-600 mb-1 block">Type</label>
                  <Select value={invoiceFilters.type} onValueChange={(v) => updateInvoiceFilter('type', v)}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="retainer">Retainer</SelectItem>
                      <SelectItem value="project">Project</SelectItem>
                      <SelectItem value="hourly">Hourly</SelectItem>
                      <SelectItem value="one_time">One Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Client Filter — Searchable Dropdown */}
                <div>
                  <label className="text-xs font-medium text-neutral-600 mb-1 block">Client</label>
                  <div className="relative" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setClientDropdownOpen(false); }}>
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-neutral-400 pointer-events-none" />
                      <input
                        type="text"
                        value={clientDropdownOpen ? clientFilterSearch : (invoiceFilters.client === 'all' ? '' : (allClientsForFilter.find(c => c._id === invoiceFilters.client)?.name || ''))}
                        onChange={(e) => setClientFilterSearch(e.target.value)}
                        placeholder="All Clients"
                        className="w-full h-9 pl-8 pr-8 text-sm border border-white/40 rounded-xl focus:outline-none focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                        data-testid="client-filter-search"
                        onFocus={() => { setClientDropdownOpen(true); setClientFilterSearch(''); }}
                      />
                      {invoiceFilters.client !== 'all' && (
                        <button 
                          onClick={() => { setInvoiceFilters({...invoiceFilters, client: 'all'}); setClientFilterSearch(''); setClientDropdownOpen(false); }}
                          className="absolute right-2 top-2 p-0.5 hover:bg-neutral-100 rounded"
                          tabIndex={-1}
                        >
                          <X className="w-3.5 h-3.5 text-neutral-400" />
                        </button>
                      )}
                    </div>
                    {clientDropdownOpen && (
                      <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto glass-card rounded-xl shadow-lg">
                        <button
                          onClick={() => { setInvoiceFilters({...invoiceFilters, client: 'all'}); setClientFilterSearch(''); setClientDropdownOpen(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-white/60 font-medium text-neutral-700"
                          tabIndex={-1}
                        >
                          All Clients
                        </button>
                        {allClientsForFilter
                          .filter(c => !clientFilterSearch || (c.name || '').toLowerCase().includes(clientFilterSearch.toLowerCase()))
                          .sort((a, b) => {
                            if (a.is_archived !== b.is_archived) return a.is_archived ? 1 : -1;
                            return (a.name || '').localeCompare(b.name || '');
                          })
                          .map(c => (
                            <button
                              key={c._id}
                              onClick={() => { setInvoiceFilters({...invoiceFilters, client: c._id}); setClientFilterSearch(''); setClientDropdownOpen(false); }}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-white/60 flex items-center justify-between ${
                                invoiceFilters.client === c._id ? 'bg-pink-50 text-pink-700' : ''
                              }`}
                              tabIndex={-1}
                            >
                              <span className="truncate">{c.name}</span>
                              {c.is_archived && <span className="text-[10px] px-1.5 py-0.5 bg-neutral-100 text-neutral-500 rounded flex-shrink-0 ml-2">archived</span>}
                            </button>
                          ))
                        }
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Date Preset */}
                <div>
                  <label className="text-xs font-medium text-neutral-600 mb-1 block">Date Range</label>
                  <Select value={invoiceDatePreset} onValueChange={handleDatePresetChange}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="current_fy">Current FY (Apr–Mar)</SelectItem>
                      <SelectItem value="current_cy">Current Calendar Year</SelectItem>
                      <SelectItem value="last_month">Last Month</SelectItem>
                      <SelectItem value="last_quarter">Last Quarter</SelectItem>
                      <SelectItem value="last_fy">Last FY</SelectItem>
                      <SelectItem value="all_time">All Time</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Date From — shown for custom or when any preset is active */}
                {invoiceDatePreset === 'custom' && (
                <>
                <div>
                  <label className="text-xs font-medium text-neutral-600 mb-1 block">From Date</label>
                  <Input
                    type="date"
                    value={invoiceFilters.dateFrom}
                    onChange={(e) => updateInvoiceFilter('dateFrom', e.target.value)}
                    className="h-9"
                  />
                </div>
                
                {/* Date To */}
                <div>
                  <label className="text-xs font-medium text-neutral-600 mb-1 block">To Date</label>
                  <Input
                    type="date"
                    value={invoiceFilters.dateTo}
                    onChange={(e) => updateInvoiceFilter('dateTo', e.target.value)}
                    className="h-9"
                  />
                </div>
                </>
                )}
              </div>
              {/* Mobile action bar */}
              <div className="md:hidden -mx-4 mt-4 p-3 border-t border-neutral-100 flex gap-2 bg-white sticky bottom-0 safe-bottom">
                {hasActiveInvoiceFilters && (
                  <Button variant="outline" onClick={resetInvoiceFilters} className="flex-1" data-testid="invoice-filter-clear">
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                )}
                <Button onClick={() => setShowInvoiceFilters(false)} className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="invoice-filter-apply">
                  Show results
                </Button>
              </div>
            </div>
            </>
          )}

          {/* Invoices Table */}
          <div className="soft-card overflow-hidden">
            {/* Mobile card list — swipeable cards on phones (<md); native table below covers desktop */}
            <div className="md:hidden p-3 space-y-2" data-testid="invoice-mobile-list">
              {filteredAndSortedInvoices.length === 0 ? (
                <div className="py-10 text-center text-sm text-neutral-500">
                  {hasActiveInvoiceFilters ? 'No invoices match your filters' : 'No invoices yet'}
                </div>
              ) : filteredAndSortedInvoices.slice(0, 100).map(inv => {
                const statusTone = inv.status === 'paid' ? 'emerald' : inv.status === 'partially_paid' ? 'amber' : inv.status === 'overdue' ? 'red' : inv.status === 'sent' ? 'blue' : 'neutral';
                const badges = [
                  { label: (inv.status || 'draft').replace('_', ' '), tone: statusTone },
                  inv.is_recurring ? { label: 'Recurring', tone: 'fuchsia' } : null,
                  inv.status === 'voided' ? { label: 'Voided', tone: 'red' } : null,
                ].filter(Boolean);
                const actions = [];
                if (permissions.canDownload) actions.push({ label: 'PDF', icon: Download, onClick: (e) => { e.stopPropagation(); handleDownloadPDF(inv._id, inv.invoice_number, inv.client_name); }, tone: 'blue', testId: `mob-dl-inv-${inv._id}` });
                if (permissions.canEditInvoices) actions.push({ label: 'Email', icon: Mail, onClick: (e) => { e.stopPropagation(); setEmailInvoice(inv); }, tone: 'fuchsia', testId: `mob-email-inv-${inv._id}` });
                if (permissions.canEditInvoices) actions.push({ label: 'WA', icon: MessageCircle, onClick: (e) => { e.stopPropagation(); handleSendInvoiceWhatsApp(inv); }, tone: 'emerald', testId: `mob-wa-inv-${inv._id}` });
                return (
                  <MobileDataCard
                    key={inv._id}
                    testId={`invoice-card-${inv._id}`}
                    title={inv.invoice_number || 'Draft'}
                    subtitle={inv.client_name}
                    badges={badges}
                    rows={[
                      { label: 'Total', value: formatCurrency(inv.total || 0), mono: true },
                      { label: 'Date', value: inv.invoice_date ? formatDate(inv.invoice_date) : '—' },
                    ]}
                    onTap={permissions.canEditInvoices && inv.status !== 'voided' ? () => { setDocModalType('invoice'); setEditingDoc(inv); setShowDocModal(true); } : undefined}
                    actions={actions}
                  />
                );
              })}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full" style={{ minWidth: '900px' }}>
                <thead className="bg-white/40 border-b">
                  <tr>
                    <th className="px-2 py-3 text-center w-10">
                      <button 
                        onClick={handleSelectAllInvoices}
                        className="p-1 hover:bg-neutral-200 rounded"
                        title={selectedInvoiceIds.length === filteredAndSortedInvoices.length ? "Deselect all" : "Select all"}
                      >
                        {selectedInvoiceIds.length === filteredAndSortedInvoices.length && filteredAndSortedInvoices.length > 0 
                          ? <CheckSquare className="w-4 h-4 text-pink-600" />
                          : <Square className="w-4 h-4 text-neutral-400" />
                        }
                      </button>
                    </th>
                    <th 
                      className="px-3 py-3 text-left text-sm font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-100 max-w-[140px]"
                      onClick={() => handleInvoiceSort('invoice_number')}
                    >
                      <div className="flex items-center gap-1">
                        Inv #
                        {invoiceSortField === 'invoice_number' && (invoiceSortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-3 text-left text-sm font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-100"
                      onClick={() => handleInvoiceSort('client_name')}
                    >
                      <div className="flex items-center gap-1">
                        Client
                        {invoiceSortField === 'client_name' && (invoiceSortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-3 text-left text-sm font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-100"
                      onClick={() => handleInvoiceSort('invoice_date')}
                    >
                      <div className="flex items-center gap-1">
                        Date
                        {invoiceSortField === 'invoice_date' && (invoiceSortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-3 text-right text-sm font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-100"
                      onClick={() => handleInvoiceSort('subtotal')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Net Amt
                        {invoiceSortField === 'subtotal' && (invoiceSortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                      </div>
                    </th>
                    <th className="px-3 py-3 text-right text-sm font-semibold text-neutral-700">
                      GST
                    </th>
                    <th 
                      className="px-3 py-3 text-right text-sm font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-100"
                      onClick={() => handleInvoiceSort('total')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Total
                        {invoiceSortField === 'total' && (invoiceSortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-3 text-left text-sm font-semibold text-neutral-700 cursor-pointer hover:bg-neutral-100"
                      onClick={() => handleInvoiceSort('status')}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {invoiceSortField === 'status' && (invoiceSortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                      </div>
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-semibold text-neutral-500">GST Filing</th>
                    <th className="px-2 py-3 text-center text-xs font-semibold text-neutral-500">TDS Filing</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold text-neutral-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredAndSortedInvoices.length === 0 ? (
                    <tr><td colSpan="13" className="px-4 py-2">
                      <EmptyState
                        icon={Receipt}
                        title={hasActiveInvoiceFilters ? 'No invoices match your filters' : 'No invoices yet'}
                        description={hasActiveInvoiceFilters ? 'Adjust filters to see more results.' : 'Create your first invoice to get started.'}
                        actionLabel={hasActiveInvoiceFilters ? 'Clear Filters' : (permissions.canCreateInvoices ? 'Create Your First Invoice' : undefined)}
                        onAction={hasActiveInvoiceFilters ? resetInvoiceFilters : (permissions.canCreateInvoices ? () => { setDocModalType('invoice'); setEditingDoc(null); setShowDocModal(true); } : undefined)}
                        testId="empty-invoices-table"
                      />
                    </td></tr>
                  ) : filteredAndSortedInvoices.map(inv => (
                  <tr key={inv._id} className={`hover:bg-white/60 ${inv.status === 'voided' ? 'opacity-50 bg-white/40' : ''}`}>
                    <td className="px-2 py-3 text-center">
                      <button 
                        onClick={() => handleToggleInvoiceSelect(inv._id)}
                        className="p-1 hover:bg-neutral-200 rounded"
                      >
                        {selectedInvoiceIds.includes(inv._id) 
                          ? <CheckSquare className="w-4 h-4 text-pink-600" />
                          : <Square className="w-4 h-4 text-neutral-400" />
                        }
                      </button>
                    </td>
                    <td className="px-3 py-3 max-w-[140px]">
                      {editingInvoiceId === inv._id && permissions.canEdit ? (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1">
                            <Input
                              value={editingInvoiceNumber}
                              onChange={(e) => {
                                setEditingInvoiceNumber(e.target.value);
                                setInvoiceNumberError(null);
                              }}
                              className="w-28 h-7 text-xs"
                              placeholder="HED2600001"
                              data-testid={`edit-invoice-number-${inv._id}`}
                            />
                            <button 
                              onClick={() => handleSaveInvoiceNumber(inv._id)}
                              disabled={savingInvoiceNumber}
                              className="p-1 hover:bg-green-50 rounded"
                              title="Save"
                              data-testid={`save-invoice-number-${inv._id}`}
                            >
                              <Check className="w-3 h-3 text-green-600" />
                            </button>
                            <button 
                              onClick={handleCancelEditInvoiceNumber}
                              className="p-1 hover:bg-red-50 rounded"
                              title="Cancel"
                              data-testid={`cancel-invoice-number-${inv._id}`}
                            >
                              <X className="w-3 h-3 text-red-600" />
                            </button>
                          </div>
                          {invoiceNumberError && (
                            <span className="text-xs text-red-600">{invoiceNumberError}</span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 group">
                          <span 
                            className={`text-sm truncate ${permissions.canEdit ? "cursor-pointer hover:text-pink-600" : ""}`}
                            onClick={() => permissions.canEdit && handleStartEditInvoiceNumber(inv)}
                            title={inv.invoice_number || ''}
                            data-testid={`invoice-number-${inv._id}`}
                          >
                            {inv.invoice_number}
                            {inv.status === 'voided' && <span className="ml-1 text-xs text-red-500 font-normal">(V)</span>}
                          </span>
                          {inv.is_recurring && (
                            <span className="px-1 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 flex-shrink-0" title={`Recurring ${inv.recurring_frequency}`}>
                              <RefreshCw className="w-2.5 h-2.5 inline" />
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <button
                        onClick={() => {
                          setActiveTab('reports');
                          setReportSubView(`client-statement:${inv.client_id}`);
                        }}
                        className="text-left text-sm hover:text-teal-600 hover:underline transition-colors truncate block max-w-[180px]"
                        title={inv.client_name}
                        data-testid={`client-link-${inv._id}`}
                      >
                        {inv.client_name}
                      </button>
                    </td>
                    <td className="px-3 py-3 text-sm">
                      {editingDateInvoiceId === inv._id ? (
                        <div className="flex items-center gap-1">
                        <input
                          type="date"
                          value={editingInvoiceDate}
                          onChange={(e) => setEditingInvoiceDate(e.target.value)}
                          className="w-28 px-1 py-1 text-xs border border-white/40 rounded focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50"
                          data-testid={`edit-invoice-date-${inv._id}`}
                        />
                        <button
                          onClick={() => handleSaveDates(inv._id)}
                          disabled={savingDates}
                          className="p-1 hover:bg-green-50 rounded"
                          title="Save dates"
                        >
                          <Check className="w-3 h-3 text-green-600" />
                        </button>
                        <button
                          onClick={handleCancelEditDates}
                          className="p-1 hover:bg-red-50 rounded"
                          title="Cancel"
                        >
                          <X className="w-3 h-3 text-red-600" />
                        </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 group">
                          <span>{inv.invoice_date ? formatDate(inv.invoice_date) : '-'}</span>
                          {permissions.canEditInvoices && inv.status !== 'voided' && (
                            <button
                              onClick={() => handleStartEditDates(inv)}
                              className="p-0.5 hover:bg-neutral-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Edit dates"
                            >
                              <Edit className="w-3 h-3 text-neutral-400" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    {/* Net Amount (Subtotal) */}
                    <td className="px-3 py-3 text-right text-sm text-neutral-600">{formatCurrency(inv.subtotal || 0)}</td>
                    {/* GST Amount */}
                    <td className="px-3 py-3 text-right text-sm text-neutral-600">{formatCurrency(inv.total_gst || 0)}</td>
                    {/* Total Receivable (after TDS deduction) */}
                    <td className="px-3 py-3 text-right text-sm font-medium">
                      <div className="flex flex-col items-end">
                        <span>{formatCurrency(inv.total || 0)}</span>
                        {inv.tds_amount > 0 && (
                          <span className="text-[10px] text-neutral-400">TDS: -{formatCurrency(inv.tds_amount)}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      {permissions.canEditInvoices && inv.status !== 'voided' ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button 
                              className={`px-2 py-1 rounded-full text-xs font-medium capitalize cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1 ${
                                inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                                inv.status === 'partially_paid' ? 'bg-amber-100 text-amber-700' :
                                inv.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                inv.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                                'bg-neutral-100 text-neutral-700'
                              }`}
                              data-testid={`invoice-status-${inv._id}`}
                            >
                              {inv.status?.replace('_', ' ')}
                              <ChevronDown className="w-3 h-3" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-44 glass-card shadow-lg">
                            <DropdownMenuItem 
                              onClick={() => handleUpdateInvoiceStatus(inv._id, 'paid', inv.invoice_number)}
                              className="flex items-center gap-2 cursor-pointer hover:bg-green-50"
                              data-testid={`mark-paid-${inv._id}`}
                            >
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span>Mark as Paid</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleUpdateInvoiceStatus(inv._id, 'partially_paid', inv.invoice_number)}
                              className="flex items-center gap-2 cursor-pointer hover:bg-amber-50"
                              data-testid={`mark-partial-${inv._id}`}
                            >
                              <CircleDollarSign className="w-4 h-4 text-amber-600" />
                              <span>Partially Paid</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleUpdateInvoiceStatus(inv._id, 'draft', inv.invoice_number)}
                              className="flex items-center gap-2 cursor-pointer hover:bg-white/60"
                              data-testid={`mark-unpaid-${inv._id}`}
                            >
                              <Clock className="w-4 h-4 text-neutral-600" />
                              <span>Mark as Unpaid</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-neutral-200" />
                            <DropdownMenuItem 
                              onClick={() => handleUpdateInvoiceStatus(inv._id, 'sent', inv.invoice_number)}
                              className="flex items-center gap-2 cursor-pointer hover:bg-blue-50"
                            >
                              <Send className="w-4 h-4 text-blue-600" />
                              <span>Mark as Sent</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleUpdateInvoiceStatus(inv._id, 'overdue', inv.invoice_number)}
                              className="flex items-center gap-2 cursor-pointer hover:bg-red-50"
                            >
                              <AlertCircle className="w-4 h-4 text-red-600" />
                              <span>Mark as Overdue</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                          inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                          inv.status === 'partially_paid' ? 'bg-amber-100 text-amber-700' :
                          inv.status === 'overdue' ? 'bg-red-100 text-red-700' :
                          inv.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                          'bg-neutral-100 text-neutral-700'
                        }`}>
                          {inv.status?.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    {/* GST Filing Status Tag */}
                    <td className="px-2 py-3 text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase cursor-pointer hover:opacity-80 transition-opacity ${
                              (inv.gst_filing_status || 'pending') === 'paid' ? 'bg-green-100 text-green-700' :
                              (inv.gst_filing_status || 'pending') === 'filed' ? 'bg-blue-100 text-blue-700' :
                              'bg-neutral-100 text-neutral-500'
                            }`}
                            data-testid={`gst-filing-${inv._id}`}
                          >
                            {(inv.gst_filing_status || 'pending')}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center" className="w-28 glass-card shadow-lg">
                          <DropdownMenuItem onClick={() => handleUpdateFilingStatus(inv._id, 'gst_filing_status', 'pending')} className="text-xs cursor-pointer">
                            <span className="w-2 h-2 rounded-full bg-gray-400 mr-2" /> Pending
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleUpdateFilingStatus(inv._id, 'gst_filing_status', 'filed')} className="text-xs cursor-pointer">
                            <span className="w-2 h-2 rounded-full bg-blue-500 mr-2" /> Filed
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleUpdateFilingStatus(inv._id, 'gst_filing_status', 'paid')} className="text-xs cursor-pointer">
                            <span className="w-2 h-2 rounded-full bg-green-500 mr-2" /> Paid
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                    {/* TDS Filing Status Tag */}
                    <td className="px-2 py-3 text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase cursor-pointer hover:opacity-80 transition-opacity ${
                              (inv.tds_filing_status || 'not_paid') === 'paid' ? 'bg-green-100 text-green-700' :
                              'bg-neutral-100 text-neutral-500'
                            }`}
                            data-testid={`tds-filing-${inv._id}`}
                          >
                            {(inv.tds_filing_status || 'not_paid') === 'paid' ? 'PAID' : 'NOT PAID'}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="center" className="w-28 glass-card shadow-lg">
                          <DropdownMenuItem onClick={() => handleUpdateFilingStatus(inv._id, 'tds_filing_status', 'not_paid')} className="text-xs cursor-pointer">
                            <span className="w-2 h-2 rounded-full bg-gray-400 mr-2" /> Not Paid
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleUpdateFilingStatus(inv._id, 'tds_filing_status', 'paid')} className="text-xs cursor-pointer">
                            <span className="w-2 h-2 rounded-full bg-green-500 mr-2" /> Paid
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-1">
                        {/* Quick Action: Record Payment - show for unpaid/partial invoices */}
                        {(inv.status === 'sent' || inv.status === 'draft' || inv.status === 'overdue' || inv.status === 'partially_paid') && permissions.canEditInvoices && (
                          <button 
                            onClick={() => {
                              setActiveTab('payments');
                              // Could also open a modal here directly
                            }}
                            className="p-1 hover:bg-teal-50 rounded" 
                            title="Record Payment"
                            data-testid={`record-payment-${inv._id}`}
                          >
                            <Banknote className="w-4 h-4 text-teal-600" />
                          </button>
                        )}
                        {/* Quick Action: Send Reminder - show for overdue invoices */}
                        {inv.status === 'overdue' && permissions.canEditInvoices && (
                          <button 
                            onClick={() => setEmailInvoice(inv)}
                            className="p-1 hover:bg-orange-50 rounded" 
                            title="Send Reminder"
                            data-testid={`send-reminder-${inv._id}`}
                          >
                            <AlertCircle className="w-4 h-4 text-orange-500" />
                          </button>
                        )}
                        {permissions.canDownload && (
                          <button 
                            onClick={() => handlePreviewPDF(inv._id, inv.invoice_number, inv.client_name)} 
                            className="p-1 hover:bg-green-50 rounded" 
                            title="Preview PDF"
                            data-testid={`preview-pdf-${inv._id}`}
                          >
                            <Eye className="w-4 h-4 text-green-600" />
                          </button>
                        )}
                        {permissions.canDownload && (
                          <button 
                            onClick={() => handleDownloadPDF(inv._id, inv.invoice_number, inv.client_name)} 
                            className="p-1 hover:bg-blue-50 rounded" 
                            title="Download PDF"
                            data-testid={`download-pdf-${inv._id}`}
                          >
                            <Download className="w-4 h-4 text-blue-600" />
                          </button>
                        )}
                        {permissions.canEditInvoices && (
                          <button 
                            onClick={() => setEmailInvoice(inv)}
                            className="p-1 hover:bg-indigo-50 rounded relative" 
                            title={inv.last_emailed_to ? `Last emailed to ${inv.last_emailed_to}` : "Send via Email"}
                            data-testid={`send-email-${inv._id}`}
                          >
                            <Mail className="w-4 h-4 text-indigo-600" />
                            {inv.last_emailed_at && (
                              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full" title="Previously sent" />
                            )}
                          </button>
                        )}
                        {inv.is_recurring && permissions.canCreateInvoices && (
                          <button 
                            onClick={() => handleGenerateRecurringInvoice(inv._id)} 
                            className="p-1 hover:bg-purple-50 rounded" 
                            title="Generate Next Invoice"
                            data-testid={`generate-recurring-${inv._id}`}
                          >
                            <RefreshCw className="w-4 h-4 text-purple-600" />
                          </button>
                        )}
                        {permissions.canEditInvoices && inv.status !== 'voided' && (
                          <button onClick={() => { setDocModalType('invoice'); setEditingDoc(inv); setShowDocModal(true); }} className="p-1 hover:bg-neutral-100 rounded" title="Edit">
                            <Edit className="w-4 h-4 text-neutral-600" />
                          </button>
                        )}
                        {permissions.canCreateInvoices && !inv.is_recurring && (
                          <button 
                            onClick={() => handleCloneInvoice(inv._id)} 
                            className="p-1 hover:bg-blue-50 rounded" 
                            title="Clone Invoice"
                          >
                            <Copy className="w-4 h-4 text-blue-600" />
                          </button>
                        )}
                        {permissions.canEditInvoices && inv.status !== 'voided' && (
                          <button
                            onClick={() => handleSendInvoiceWhatsApp(inv)}
                            className="p-1 hover:bg-emerald-50 rounded"
                            title="Send via WhatsApp"
                            data-testid={`wa-invoice-${inv._id}`}
                          >
                            <MessageCircle className="w-4 h-4 text-emerald-600" />
                          </button>
                        )}
                        {permissions.canDeleteInvoices && inv.status !== 'voided' && (
                          <button 
                            onClick={() => handleVoidInvoice(inv._id, inv.invoice_number)} 
                            className="p-1 hover:bg-orange-50 rounded" 
                            title="Void Invoice"
                            data-testid={`void-invoice-${inv._id}`}
                          >
                            <Ban className="w-4 h-4 text-orange-600" />
                          </button>
                        )}
                        {permissions.canEditInvoices && inv.status === 'voided' && (
                          <button 
                            onClick={() => handleUnvoidInvoice(inv._id, inv.invoice_number, inv.previous_status)} 
                            className="p-1 hover:bg-green-50 rounded" 
                            title={`Restore to ${(inv.previous_status || 'draft').replace('_', ' ')}`}
                            data-testid={`unvoid-invoice-${inv._id}`}
                          >
                            <RotateCcw className="w-4 h-4 text-green-600" />
                          </button>
                        )}
                        {permissions.canDeleteInvoices && (
                          <button onClick={() => handleDeleteDocument('invoice', inv._id)} className="p-1 hover:bg-red-50 rounded" title="Delete">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Infinite Scroll Sentinel */}
          <div className="flex flex-col items-center gap-2 px-4 py-3 border-t bg-white/40" data-testid="invoice-pagination">
            <div className="text-sm text-neutral-600">
              Showing <span className="font-medium">{invoices.length}</span> of <span className="font-medium">{invoiceTotal}</span> invoices
            </div>
            {invoiceHasMore && (
              <div ref={loadMoreRef} className="flex items-center justify-center py-2" data-testid="load-more-sentinel">
                {loadingMore && (
                  <div className="flex items-center gap-2 text-sm text-neutral-500">
                    <RefreshCw className="w-4 h-4 animate-spin" /> Loading more...
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        </div>
      )}

      {/* Estimates Tab */}
      {activeTab === 'estimates' && permissions.canViewEstimates && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-[#1a1a2e]">Estimates</h2>
            {permissions.canCreateEstimates && (
              <Button onClick={() => { setDocModalType('estimate'); setEditingDoc(null); setShowDocModal(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Create Estimate
              </Button>
            )}
          </div>
          <div className="soft-card overflow-hidden">
            <div className="sm:hidden px-4 py-2 bg-white/40 border-b border-white/30 text-[10px] text-neutral-400 text-center">Swipe left to see all columns</div>
            <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: '600px' }}>
              <thead className="bg-white/40 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Estimate #</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Client</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700 whitespace-nowrap">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Valid Until</th>
                  {(permissions.canEdit || permissions.canDelete) && (
                    <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {estimates.length === 0 ? (
                  <tr><td colSpan={permissions.canEditEstimates || permissions.canDeleteEstimates || permissions.canDownload ? 6 : 5} className="px-4 py-2">
                    <EmptyState icon={FileText} title="No estimates yet" description="Create your first estimate to get started." testId="empty-estimates-table" />
                  </td></tr>
                ) : estimates.map(est => (
                  <tr key={est._id} className="hover:bg-white/60">
                    <td className="px-4 py-3 font-medium">{est.estimate_number}</td>
                    <td className="px-4 py-3">{est.client_name}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(est.total)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                        est.status === 'accepted' ? 'bg-green-100 text-green-700' :
                        est.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        est.status === 'converted' ? 'bg-purple-100 text-purple-700' :
                        'bg-neutral-100 text-neutral-700'
                      }`}>
                        {est.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-neutral-600">{est.valid_until ? formatDate(est.valid_until) : '-'}</td>
                    {(permissions.canEditEstimates || permissions.canDeleteEstimates || permissions.canDownload) && (
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {permissions.canDownload && (
                            <button
                              onClick={() => handleDownloadEstimatePDF(est._id, est.estimate_number, est.client_name)}
                              className="p-1 hover:bg-blue-50 rounded"
                              title="Download PDF"
                              data-testid={`download-estimate-${est._id}`}
                            >
                              <Download className="w-4 h-4 text-blue-600" />
                            </button>
                          )}
                          {permissions.canEditEstimates && (
                            <button
                              onClick={() => setEmailEstimate(est)}
                              className="p-1 hover:bg-indigo-50 rounded"
                              title="Send via Email"
                              data-testid={`email-estimate-${est._id}`}
                            >
                              <Mail className="w-4 h-4 text-indigo-600" />
                            </button>
                          )}
                          {permissions.canCreateInvoices && est.status !== 'converted' && (
                            <button
                              onClick={() => handleConvertEstimateToInvoice(est)}
                              className="p-1 hover:bg-emerald-50 rounded"
                              title="Convert to Invoice"
                              data-testid={`convert-estimate-${est._id}`}
                            >
                              <ArrowRight className="w-4 h-4 text-emerald-600" />
                            </button>
                          )}
                          {permissions.canEditEstimates && (
                            <button onClick={() => { setDocModalType('estimate'); setEditingDoc(est); setShowDocModal(true); }} className="p-1 hover:bg-neutral-100 rounded" title="Edit">
                              <Edit className="w-4 h-4 text-neutral-600" />
                            </button>
                          )}
                          {permissions.canDeleteEstimates && (
                            <button onClick={() => handleDeleteDocument('estimate', est._id)} className="p-1 hover:bg-red-50 rounded" title="Delete">
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'vendors' && permissions.canViewPOs && (
        <VendorsManager api={api} canDelete={permissions.canDeletePOs} />
      )}

      {activeTab === 'po_categories' && permissions.canViewPOs && (
        <POCategoriesManager api={api} canDelete={permissions.canDeletePOs} />
      )}

      {activeTab === 'purchase_orders' && permissions.canViewPOs && (
        <div>
          <datalist id="po-vendor-suggestions">
            {vendorSuggestions.map(v => (
              <option key={v._id} value={v.name}>{v.contact_person ? `${v.contact_person} · ${v.phone || v.email || ''}` : (v.phone || v.email || '')}</option>
            ))}
          </datalist>
          {(() => {
            // Fetch vendor suggestions once when the PO tab activates
            if (vendorSuggestions.length === 0) {
              api.get('/api/admin/accounting/vendors').then(r => setVendorSuggestions(r.data || [])).catch(()=>{});
            }
            return null;
          })()}
          <POApprovalPanel api={api} onDecided={() => fetchPurchaseOrders({ silent: true })} />
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-[#1a1a2e]">
                Purchase Orders
                {permissions.viewOwnPOsOnly && <span className="text-sm font-normal text-neutral-500 ml-2">(Your POs only)</span>}
              </h2>
              {selectedPOIds.length > 0 && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-fuchsia-100 border border-fuchsia-200" data-testid="po-bulk-toolbar">
                  <span className="text-xs font-semibold text-fuchsia-900">{selectedPOIds.length} selected</span>
                  <button
                    onClick={async () => {
                      try {
                        await api.post('/api/admin/accounting/purchase-orders/bulk-update-status', { po_ids: selectedPOIds, status: 'completed' });
                        toast.success(`${selectedPOIds.length} PO(s) marked completed`);
                        setSelectedPOIds([]); fetchPurchaseOrders?.();
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk update failed'); }
                    }}
                    className="text-xs px-2 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                    data-testid="po-bulk-complete"
                  >Mark Completed</button>
                  <button
                    onClick={async () => {
                      try {
                        await api.post('/api/admin/accounting/purchase-orders/bulk-update-status', { po_ids: selectedPOIds, status: 'paid' });
                        toast.success(`${selectedPOIds.length} PO(s) marked paid`);
                        setSelectedPOIds([]); fetchPurchaseOrders?.();
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk update failed'); }
                    }}
                    className="text-xs px-2 py-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    data-testid="po-bulk-paid"
                  >Mark Paid</button>
                  <button onClick={() => setSelectedPOIds([])} className="text-xs px-2 py-1 rounded-full bg-neutral-200 hover:bg-neutral-300 text-neutral-700">Clear</button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPOFilters(!showPOFilters)}
                className={hasActivePOFilters ? 'border-pink-500 text-pink-600' : ''}
                data-testid="po-filter-toggle"
              >
                <Filter className="w-4 h-4 mr-1" />
                Filters {hasActivePOFilters && `(${[poFilters.status !== 'all', poFilters.approval_status !== 'all', poFilters.category !== 'all', !!poFilters.vendor, !!poFilters.dateFrom, !!poFilters.dateTo, !!poFilters.search].filter(Boolean).length})`}
              </Button>
              {hasActivePOFilters && (
                <Button variant="ghost" size="sm" onClick={resetPOFilters} data-testid="po-filter-reset-inline">
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await api.get('/api/admin/accounting/purchase-orders/export-approvals.csv', { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'po-approvals.csv';
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('Approval log downloaded');
                  } catch (e) {
                    toast.error('Export failed');
                  }
                }}
                data-testid="po-export-csv"
              >
                <Download className="w-4 h-4 mr-2" /> Export approvals CSV
              </Button>
              {permissions.canCreatePOs && (
                <Button onClick={() => { setDocModalType('purchase_order'); setEditingDoc(null); setShowDocModal(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> Create PO
                </Button>
              )}
            </div>
          </div>

          {/* PO Filter Panel — bottom-sheet on mobile, inline on desktop */}
          {showPOFilters && (
            <>
              <div className="md:hidden fixed inset-0 bg-black/40 z-[70]" onClick={() => setShowPOFilters(false)} data-testid="po-filter-backdrop" />
              <div
                className="max-md:fixed max-md:left-0 max-md:right-0 max-md:bottom-0 max-md:z-[71] max-md:bg-white max-md:rounded-t-2xl max-md:shadow-2xl max-md:max-h-[85vh] max-md:overflow-y-auto md:bg-white/40 md:rounded-xl md:border md:border-white/40 p-4 mb-4"
                data-testid="po-filter-sheet"
              >
                <div className="md:hidden -mx-4 -mt-4 pt-3 pb-2 border-b border-neutral-100 mb-3 sticky top-0 bg-white z-10">
                  <div className="flex justify-center mb-2"><div className="h-1.5 w-10 rounded-full bg-neutral-300" /></div>
                  <div className="px-4 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-neutral-900">Filter POs</h3>
                    <button onClick={() => setShowPOFilters(false)} className="p-1.5 rounded-lg hover:bg-neutral-100" data-testid="po-filter-close">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-xs font-medium text-neutral-600 mb-1 block">Search</label>
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
                      <Input placeholder="PO #, vendor, client, notes…" value={poFilters.search}
                        onChange={(e) => setPoFilters(f => ({ ...f, search: e.target.value }))} className="pl-8 h-9" data-testid="po-filter-search" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600 mb-1 block">Status</label>
                    <Select value={poFilters.status} onValueChange={(v) => setPoFilters(f => ({ ...f, status: v }))}>
                      <SelectTrigger className="h-9" data-testid="po-filter-status"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600 mb-1 block">Approval</label>
                    <Select value={poFilters.approval_status} onValueChange={(v) => setPoFilters(f => ({ ...f, approval_status: v }))}>
                      <SelectTrigger className="h-9" data-testid="po-filter-approval"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="all">All Approvals</SelectItem>
                        <SelectItem value="not_submitted">Needs approval</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600 mb-1 block">Category</label>
                    <Select value={poFilters.category} onValueChange={(v) => setPoFilters(f => ({ ...f, category: v }))}>
                      <SelectTrigger className="h-9" data-testid="po-filter-category"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white max-h-[240px] overflow-auto">
                        <SelectItem value="all">All Categories</SelectItem>
                        {(poCategories || []).map(c => (
                          <SelectItem key={c._id || c.name} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600 mb-1 block">Vendor</label>
                    <Input placeholder="Vendor contains…" value={poFilters.vendor}
                      onChange={(e) => setPoFilters(f => ({ ...f, vendor: e.target.value }))} className="h-9" data-testid="po-filter-vendor" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600 mb-1 block">From Date</label>
                    <Input type="date" value={poFilters.dateFrom}
                      onChange={(e) => setPoFilters(f => ({ ...f, dateFrom: e.target.value }))} className="h-9" data-testid="po-filter-date-from" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-neutral-600 mb-1 block">To Date</label>
                    <Input type="date" value={poFilters.dateTo}
                      onChange={(e) => setPoFilters(f => ({ ...f, dateTo: e.target.value }))} className="h-9" data-testid="po-filter-date-to" />
                  </div>
                </div>
                <div className="md:hidden -mx-4 mt-4 p-3 border-t border-neutral-100 flex gap-2 bg-white sticky bottom-0 safe-bottom">
                  {hasActivePOFilters && (
                    <Button variant="outline" onClick={resetPOFilters} className="flex-1" data-testid="po-filter-clear">
                      <X className="w-4 h-4 mr-1" /> Clear
                    </Button>
                  )}
                  <Button onClick={() => setShowPOFilters(false)} className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="po-filter-apply">
                    Show results
                  </Button>
                </div>
              </div>
            </>
          )}
          <POQuickFilterChips pos={poFilteredBase} value={poPaymentChip} onChange={setPoPaymentChip} />
          <div className="soft-card overflow-hidden">
            {/* Mobile filter-chip strip — tap to clear active filters */}
            <StickyFilterChips
              chips={[
                poFilters.status !== 'all' ? { key: 'po-status', label: `Status: ${poFilters.status}`, onClear: () => setPoFilters(f => ({ ...f, status: 'all' })) } : null,
                poFilters.approval_status !== 'all' ? { key: 'po-approval', label: `Approval: ${poFilters.approval_status.replace('_', ' ')}`, onClear: () => setPoFilters(f => ({ ...f, approval_status: 'all' })) } : null,
                poFilters.category !== 'all' ? { key: 'po-category', label: `Category: ${poFilters.category}`, onClear: () => setPoFilters(f => ({ ...f, category: 'all' })) } : null,
                poFilters.vendor ? { key: 'po-vendor', label: `Vendor: ${poFilters.vendor}`, onClear: () => setPoFilters(f => ({ ...f, vendor: '' })) } : null,
                poFilters.search ? { key: 'po-search', label: `Search: ${poFilters.search}`, onClear: () => setPoFilters(f => ({ ...f, search: '' })) } : null,
                poFilters.dateFrom || poFilters.dateTo ? { key: 'po-date', label: `Date: ${poFilters.dateFrom || '…'} → ${poFilters.dateTo || '…'}`, onClear: () => setPoFilters(f => ({ ...f, dateFrom: '', dateTo: '' })) } : null,
              ].filter(Boolean)}
              onClearAll={hasActivePOFilters ? resetPOFilters : undefined}
            />
            {/* Mobile card list — hidden on md+; the table below covers desktop */}
            <div className="md:hidden p-3 space-y-2" data-testid="po-mobile-list">
              {displayedPurchaseOrders.length === 0 ? (
                <div className="py-10 text-center text-sm text-neutral-500">No purchase orders yet</div>
              ) : displayedPurchaseOrders.slice(0, 100).map(po => {
                const canEditPO = permissions.canEditPOs && (!permissions.viewOwnPOsOnly || po.created_by === currentEmployeeId);
                const isUnapproved = !po.approval_status || po.approval_status === 'not_submitted';
                const isPending = po.approval_status === 'pending';
                // Payment lifecycle badge — mirrors the desktop authority.
                const poStatus = (po.status || '').toLowerCase();
                const paid = poStatus === 'paid' || poStatus === 'completed';
                const cancelled = poStatus === 'cancelled' || poStatus === 'rejected';
                let due = null;
                try { if (po.due_date) due = new Date(po.due_date); } catch { due = null; }
                const overdue = !paid && !cancelled && due && !isNaN(due) && due < new Date();
                const paymentBadge = paid
                  ? { label: '● Paid', tone: 'emerald' }
                  : cancelled
                    ? { label: '✕ ' + (poStatus === 'rejected' ? 'Rejected' : 'Cancelled'), tone: 'neutral' }
                    : overdue
                      ? { label: '⚠ Overdue', tone: 'red' }
                      : { label: '○ Pending', tone: 'amber' };
                const badges = [
                  paymentBadge,
                  po.category ? { label: po.category, tone: 'fuchsia' } : null,
                  isUnapproved ? { label: 'Needs approval', tone: 'fuchsia' } : null,
                  isPending ? { label: 'Approval pending', tone: 'amber' } : null,
                  po.approval_status === 'approved' ? { label: 'Approved', tone: 'emerald' } : null,
                  po.approval_status === 'rejected' ? { label: 'Approval rejected', tone: 'red' } : null,
                ].filter(Boolean);
                const actions = [];
                if (canEditPO && isUnapproved) actions.push({ label: 'Send', icon: Send, onClick: (e) => { e.stopPropagation(); setSubmitPO(po); }, tone: 'fuchsia', testId: `mob-submit-po-${po._id}` });
                if (mode === 'admin') actions.push({ label: `Proof${po.payment_proofs?.length ? ` (${po.payment_proofs.length})` : ''}`, icon: Paperclip, onClick: (e) => { e.stopPropagation(); setProofPO(po); }, tone: 'fuchsia', testId: `mob-proof-po-${po._id}` });
                if (permissions.canCreateInvoices) actions.push({ label: 'Invoice', icon: Receipt, onClick: (e) => { e.stopPropagation(); handleCreateInvoiceFromPO(po._id, po.po_number); }, tone: 'blue', testId: `mob-inv-po-${po._id}` });
                return (
                  <MobileDataCard
                    key={po._id}
                    testId={`po-card-${po._id}`}
                    title={po.po_number}
                    subtitle={`${po.vendor_name || '—'}${po.client_name ? ` · for ${po.client_name}` : ''}`}
                    badges={badges}
                    rows={[
                      { label: 'Amount', value: formatCurrency(po.total || 0), mono: true },
                      { label: 'PO Date', value: po.po_date ? formatDate(po.po_date) : '—' },
                      { label: 'Due', value: po.due_date ? formatDate(po.due_date) : '—' },
                    ]}
                    onTap={canEditPO ? () => { setDocModalType('purchase_order'); setEditingDoc(po); setShowDocModal(true); } : undefined}
                    actions={actions}
                  />
                );
              })}
            </div>
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full" style={{ minWidth: '700px' }}>
              <thead className="bg-white/40 border-b">
                <tr>
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={purchaseOrders.length > 0 && selectedPOIds.length === purchaseOrders.length}
                      onChange={(e) => setSelectedPOIds(e.target.checked ? purchaseOrders.map(p => p._id) : [])}
                      className="w-4 h-4 accent-fuchsia-600"
                      data-testid="po-select-all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">PO # / Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Vendor / Client</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Category</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700 whitespace-nowrap">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Payment</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Approval</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Due Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {displayedPurchaseOrders.length === 0 ? (
                  <tr><td colSpan="9" className="px-4 py-8 text-center text-neutral-500">
                    {permissions.viewOwnPOsOnly ? 'You have no purchase orders yet' : 'No purchase orders yet'}
                  </td></tr>
                ) : displayedPurchaseOrders.map(po => {
                  // For regular employees, they can only edit their own POs
                  const canEditThisPO = permissions.canEditPOs && (!permissions.viewOwnPOsOnly || po.created_by === currentEmployeeId || po.employee_id === currentEmployeeId);
                  const canDownloadThisPO = permissions.canDownload && (!permissions.viewOwnPOsOnly || po.created_by === currentEmployeeId || po.employee_id === currentEmployeeId);
                  
                  return (
                  <React.Fragment key={po._id}>
                  <tr className="hover:bg-white/60">
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedPOIds.includes(po._id)}
                        onChange={(e) => setSelectedPOIds(e.target.checked ? [...selectedPOIds, po._id] : selectedPOIds.filter(x => x !== po._id))}
                        className="w-4 h-4 accent-fuchsia-600"
                        data-testid={`po-select-${po._id}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="font-semibold text-[#1a1a2e]">{po.po_number}</span>
                        <span className="text-[11px] text-neutral-500">{po.po_date ? formatDate(po.po_date) : (po.created_at ? formatDate(po.created_at) : '—')}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm text-[#1a1a2e] font-medium truncate max-w-[220px]" title={po.vendor_name}>{po.vendor_name || '—'}</span>
                        <span className="text-[11px] text-neutral-500 truncate max-w-[220px]" title={po.client_name || 'No client'}>
                          {po.client_name ? `for ${po.client_name}` : 'Internal / no client'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {po.category ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-100">
                          {po.category}
                        </span>
                      ) : <span className="text-xs text-neutral-400 italic">Uncategorised</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex flex-col items-end">
                        <span className="font-semibold tabular-nums text-[#1a1a2e]">{formatCurrency(po.total)}</span>
                        {po.gst_mode && (
                          <span className="text-[10px] text-neutral-400 uppercase tracking-wide">{po.gst_mode === 'inclusive' ? 'incl. GST' : 'excl. GST'}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <POPaymentBadgeCell
                        po={po}
                        canEdit={permissions.canEditPOs && (!permissions.viewOwnPOsOnly || po.created_by === currentEmployeeId || po.employee_id === currentEmployeeId)}
                        onUpdated={(fresh) => {
                          // Optimistically swap the fresh doc into the visible list
                          setPurchaseOrders(prev => prev.map(x => x._id === fresh._id ? { ...x, ...fresh } : x));
                        }}
                        formatDate={formatDate}
                        onOpenProofs={mode === 'admin' ? () => setProofPO(po) : undefined}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium capitalize w-fit ${
                          po.status === 'completed' ? 'bg-green-100 text-green-700' :
                          po.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                          po.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          po.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-neutral-100 text-neutral-700'
                        }`}>
                          {po.status}
                        </span>
                        {po.linked_invoice_id && (
                          <span className="text-[10px] text-blue-700" title="An invoice has been generated from this PO">
                            ↗ Invoice created
                          </span>
                        )}
                        {(!po.approval_status || po.approval_status === 'not_submitted') && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-fuchsia-700 bg-fuchsia-100 border border-fuchsia-200 rounded-full px-2 py-0.5 animate-pulse" title="This PO must be submitted for approval before it can be marked completed or paid">
                            ⚠ Approval required
                          </span>
                        )}
                        {po.approval_status === 'pending' && (
                          <span className="text-[10px] text-amber-700" title={`${(po.approvals||[]).length}/${(po.approver_emails||[]).length} decided · mode: ${po.approval_mode || 'any'}`}>
                            ⏳ {(po.approvals||[]).length}/{(po.approver_emails||[]).length} signed
                          </span>
                        )}
                        {po.approval_status === 'approved' && (po.approvals||[]).length > 0 && (
                          <span className="text-[10px] text-emerald-700" title={(po.approvals||[]).map(a=>`${a.email}: ${a.decision}`).join('\n')}>
                            ✓ Signed by {(po.approvals||[]).filter(a=>a.decision==='approved').map(a=>a.email.split('@')[0]).join(', ')}
                          </span>
                        )}
                        {po.approval_status === 'rejected' && (
                          <span className="text-[10px] text-red-700">
                            ✗ Rejected by {(po.approvals||[]).find(a=>a.decision==='rejected')?.email.split('@')[0]}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-600 whitespace-nowrap">{po.due_date ? formatDate(po.due_date) : <span className="text-neutral-400">—</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {canDownloadThisPO && (
                          <button 
                            onClick={() => handleDownloadPOForCustomer(po._id, po.po_number)} 
                            className="p-1 hover:bg-blue-50 rounded"
                            title="Download for Customer"
                            data-testid={`download-po-${po._id}`}
                          >
                            <Download className="w-4 h-4 text-blue-600" />
                          </button>
                        )}
                        {mode === 'admin' && (
                          <button
                            onClick={() => setProofPO(po)}
                            className="relative p-1 hover:bg-fuchsia-50 rounded"
                            title={po.payment_proofs?.length ? `${po.payment_proofs.length} payment proof(s)` : 'Attach payment proof'}
                            data-testid={`po-payment-proof-btn-${po._id}`}
                          >
                            <Paperclip className={`w-4 h-4 ${po.payment_proofs?.length ? 'text-fuchsia-600' : 'text-neutral-400'}`} />
                            {po.payment_proofs?.length > 0 && (
                              <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-fuchsia-600 text-white text-[9px] font-bold leading-[14px] text-center">{po.payment_proofs.length}</span>
                            )}
                          </button>
                        )}
                        {canEditThisPO && (
                          <button onClick={() => { setDocModalType('purchase_order'); setEditingDoc(po); setShowDocModal(true); }} className="p-1 hover:bg-neutral-100 rounded" title="Edit">
                            <Edit className="w-4 h-4 text-neutral-600" />
                          </button>
                        )}
                        {(po.approvals?.length > 0 || po.approval_status) && (
                          <button
                            onClick={() => setExpandedPOId(expandedPOId === po._id ? null : po._id)}
                            className={`p-1 rounded ${expandedPOId === po._id ? 'bg-fuchsia-100' : 'hover:bg-neutral-100'}`}
                            title="Approval history"
                            data-testid={`po-history-toggle-${po._id}`}
                          >
                            <Clock className={`w-4 h-4 ${expandedPOId === po._id ? 'text-fuchsia-700' : 'text-neutral-600'}`} />
                          </button>
                        )}
                        {canEditThisPO && !['approved', 'completed'].includes(po.status) && po.approval_status !== 'pending' && po.approval_status !== 'approved' && (
                          <button
                            onClick={() => setSubmitPO(po)}
                            className="px-2 py-1 rounded-md bg-fuchsia-100 hover:bg-fuchsia-200 text-fuchsia-700 flex items-center gap-1"
                            title="This PO must be submitted for approval"
                            data-testid={`submit-po-${po._id}`}
                          >
                            <Send className="w-3.5 h-3.5" />
                            <span className="text-[10px] font-semibold uppercase tracking-wider">Send</span>
                          </button>
                        )}
                        {permissions.canCreateInvoices && (
                          <button
                            onClick={() => {
                              if (po.linked_invoice_id && !window.confirm('An invoice was already created from this PO. Create another draft?')) return;
                              handleCreateInvoiceFromPO(po._id, po.po_number);
                            }}
                            className="p-1 hover:bg-blue-50 rounded"
                            title="Create invoice from this PO"
                            data-testid={`make-invoice-po-${po._id}`}
                          >
                            <Receipt className="w-4 h-4 text-blue-600" />
                          </button>
                        )}
                        {permissions.canDeletePOs && (
                          <button onClick={() => handleDeleteDocument('purchase_order', po._id)} className="p-1 hover:bg-red-50 rounded" title="Delete">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedPOId === po._id && (
                    <tr className="bg-fuchsia-50/40" data-testid={`po-history-row-${po._id}`}>
                      <td colSpan="9" className="px-8 py-4">
                        <POApprovalHistory po={po} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                )})}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      <POPaymentProofsDialog po={proofPO} onClose={() => setProofPO(null)} onChange={handleProofsChange} />

      {/* Retainers Tab */}
      {activeTab === 'retainers' && permissions.canViewRetainers && (
        <div>
          <h2 className="text-lg font-bold text-[#1a1a2e] mb-4">Client Retainers</h2>
          <div className="soft-card overflow-hidden">
            <div className="sm:hidden px-4 py-2 bg-white/40 border-b border-white/30 text-[10px] text-neutral-400 text-center">Swipe left to see all columns</div>
            <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: '650px' }}>
              <thead className="bg-white/40 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Client</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700 whitespace-nowrap">Monthly Retainer</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700 whitespace-nowrap">Annual Value</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Billing Cycle</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Status</th>
                  {permissions.canEditRetainers && (
                    <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700 whitespace-nowrap">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {(() => {
                  // Use retainerClients for employee mode, clients for admin
                  const displayClients = mode === 'admin' ? clients : retainerClients;
                  return displayClients.map(client => {
                    // Get retainer value from either field for compatibility
                    const retainerValue = client.retainer_value || client.retainer || 0;
                    // Status is active if retainer value exists, unless explicitly set to inactive
                    const isActive = client.retainer_is_active !== false && retainerValue > 0;
                    
                    return (
                    <tr key={client._id} className="hover:bg-white/60">
                      <td className="px-4 py-3 font-medium">{client.name}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(retainerValue)}</td>
                      <td className="px-4 py-3 text-right font-medium text-green-600">{formatCurrency(retainerValue * 12)}</td>
                      <td className="px-4 py-3 capitalize">{client.retainer_billing_cycle || 'Monthly'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-neutral-100 text-neutral-700'
                        }`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      {permissions.canEditRetainers && (
                        <td className="px-4 py-3">
                          <button
                            onClick={() => { setSelectedClient(client); setShowRetainerModal(true); }}
                            className="p-1 hover:bg-neutral-100 rounded"
                          >
                            <Edit className="w-4 h-4 text-neutral-600" />
                          </button>
                        </td>
                      )}
                    </tr>
                  )});
                })()}
              </tbody>
              <tfoot className="bg-neutral-100 border-t-2">
                <tr className="font-bold">
                  <td className="px-4 py-3">TOTAL</td>
                  <td className="px-4 py-3 text-right">{formatCurrency((mode === 'admin' ? clients : retainerClients).reduce((sum, c) => sum + (c.retainer_value || c.retainer || 0), 0))}</td>
                  <td className="px-4 py-3 text-right text-green-700">{formatCurrency((mode === 'admin' ? clients : retainerClients).reduce((sum, c) => sum + (c.retainer_value || c.retainer || 0), 0) * 12)}</td>
                  <td colSpan={permissions.canEditRetainers ? 4 : 3}></td>
                </tr>
              </tfoot>
            </table>
            </div>
          </div>
        </div>
      )}


      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <ExpensesManager />
      )}

      {/* Salaries Tab */}
      {activeTab === 'salaries' && permissions.canViewSalaries && salaries && (
        <div>
          <h2 className="text-lg font-bold text-[#1a1a2e] mb-4">Employee Salaries</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
            <MetricCard
              title="Total Annual CTC"
              value={formatCurrency(salaries.totals.total_annual_ctc)}
              icon={DollarSign}
              color="blue"
            />
            <MetricCard
              title="Monthly Salary"
              value={formatCurrency(salaries.totals.total_monthly_salary)}
              icon={Calendar}
              color="green"
            />
            <MetricCard
              title="Employee Count"
              value={salaries.totals.employee_count}
              icon={Users}
              color="purple"
            />
          </div>
          <div className="soft-card overflow-hidden">
            <div className="sm:hidden px-4 py-2 bg-white/40 border-b border-white/30 text-[10px] text-neutral-400 text-center">Swipe left to see all columns</div>
            <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: '650px' }}>
              <thead className="bg-white/40 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Employee</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Designation</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Department</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Annual CTC</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Monthly Salary</th>
                  {permissions.canEditSalaries && (
                    <th className="px-4 py-3 text-center text-sm font-semibold text-neutral-700">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {salaries.employees.map(emp => (
                  <SalaryRow 
                    key={emp._id} 
                    employee={emp}
                    canEdit={permissions.canEditSalaries}
                    onUpdate={async (newSalary) => {
                      if (!permissions.canEditSalaries) {
                        toast.error('You do not have permission to edit salaries');
                        return;
                      }
                      try {
                        await api.put(`${apiPrefix}/accounting/salaries/${emp._id}`, {
                          monthly_salary: newSalary
                        });
                        toast.success('Salary updated successfully');
                        fetchSalaries({ silent: true });
                      } catch (error) {
                        toast.error('Failed to update salary');
                      }
                    }}
                  />
                ))}
              </tbody>
              <tfoot className="bg-neutral-100 border-t-2">
                <tr className="font-bold">
                  <td colSpan="3" className="px-4 py-3">TOTAL</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(salaries.totals.total_annual_ctc)}</td>
                  <td className="px-4 py-3 text-right text-green-700">{formatCurrency(salaries.totals.total_monthly_salary)}</td>
                  {permissions.canEditSalaries && <td className="px-4 py-3"></td>}
                </tr>
              </tfoot>
            </table>
            </div>
          </div>
        </div>
      )}

      {/* Document Modal - only show if user has any create or edit permissions */}
      {(permissions.canCreateInvoices || permissions.canCreateEstimates || permissions.canCreatePOs || 
        permissions.canEditInvoices || permissions.canEditEstimates || permissions.canEditPOs) && (
        <DocumentModal
          isOpen={showDocModal}
          onClose={() => { setShowDocModal(false); setEditingDoc(null); }}
          type={docModalType}
          document={editingDoc}
          clients={allClientsForFilter.length > 0 ? allClientsForFilter.map(c => ({ _id: c._id, name: c.name, company_name: c.name, company_state: c.company_state, is_archived: c.is_archived })) : clients}
          onSave={handleSaveDocument}
          poCategories={poCategories}
          onCreatePOCategory={handleCreatePOCategory}
          vendors={vendorSuggestions}
        />
      )}

      {/* Retainer Modal - only show if user can edit retainers */}
      {permissions.canEditRetainers && (
        <RetainerModal
          isOpen={showRetainerModal}
          onClose={() => { setShowRetainerModal(false); setSelectedClient(null); }}
          client={selectedClient}
          onSave={handleSaveRetainer}
        />
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete {selectedInvoiceIds.length} Invoice{selectedInvoiceIds.length !== 1 ? 's' : ''}?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-neutral-600">
              Are you sure you want to permanently delete {selectedInvoiceIds.length} selected invoice{selectedInvoiceIds.length !== 1 ? 's' : ''}? 
              This action cannot be undone.
            </p>
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>This will permanently remove all selected invoice records and their associated data.</span>
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowBulkDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDeleteInvoices}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete {selectedInvoiceIds.length} Invoice{selectedInvoiceIds.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Clone Confirmation Dialog */}
      <Dialog open={showBulkCloneConfirm} onOpenChange={setShowBulkCloneConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Copy className="w-5 h-5" />
              Clone {selectedInvoiceIds.length} Invoice{selectedInvoiceIds.length !== 1 ? 's' : ''}?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-neutral-600 mb-4">
              This will create {selectedInvoiceIds.length} new invoice{selectedInvoiceIds.length !== 1 ? 's' : ''} with:
            </p>
            <ul className="space-y-2 text-sm text-neutral-600">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                New sequential invoice numbers (HED format)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Today&apos;s date as invoice date
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Due date set to 30 days from today
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                Status set to &quot;Draft&quot;
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                All line items and amounts copied
              </li>
            </ul>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-sm text-blue-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>Oldest selected invoices will be cloned first to maintain proper sequence.</span>
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowBulkCloneConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkCloneInvoices}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Copy className="w-4 h-4 mr-2" />
              Clone {selectedInvoiceIds.length} Invoice{selectedInvoiceIds.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={showPdfPreview} onOpenChange={handleClosePdfPreview}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-4 border-b flex-shrink-0">
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Preview: {previewInvoiceNumber}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-neutral-100">
            {loadingPreview ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                  <span className="text-neutral-600">Loading PDF...</span>
                </div>
              </div>
            ) : previewPdfUrl ? (
              <iframe
                src={previewPdfUrl}
                className="w-full h-full border-0"
                title={`Preview ${previewInvoiceNumber}`}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-neutral-500">
                Failed to load preview
              </div>
            )}
          </div>
          <DialogFooter className="p-4 border-t flex-shrink-0 gap-2">
            <Button 
              variant="outline" 
              onClick={handleDownloadFromPreview}
              disabled={!previewPdfUrl || loadingPreview}
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </Button>
            <Button variant="outline" onClick={handleClosePdfPreview}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zoho Invoice Import */}
      <ZohoInvoiceImport
        open={showZohoImport}
        onClose={() => setShowZohoImport(false)}
        onImportComplete={() => { fetchInvoices(false, { silent: true }); }}
      />

      {/* Zoho Invoice Details Report Import (XLSX/CSV) */}
      <ZohoReportImport
        open={showZohoReportImport}
        onClose={() => setShowZohoReportImport(false)}
        onComplete={() => { fetchInvoices(false, { silent: true }); }}
      />

      {/* Zoho Import Batch History + Rollback */}
      <ZohoImportHistory
        open={showImportHistory}
        onClose={() => setShowImportHistory(false)}
        onRefresh={() => { fetchInvoices(false, { silent: true }); }}
      />

      {/* Send Invoice Email Dialog */}
      <SendInvoiceEmail
        isOpen={!!emailInvoice}
        onClose={(sent) => {
          setEmailInvoice(null);
          if (sent) fetchInvoices(false, { silent: true });
        }}
        invoice={emailInvoice}
      />

      {/* Send Estimate Email Dialog — reuses SendInvoiceEmail via docType prop */}
      <SendInvoiceEmail
        isOpen={!!emailEstimate}
        onClose={(sent) => {
          setEmailEstimate(null);
          if (sent) fetchEstimates?.({ silent: true });
        }}
        invoice={emailEstimate}
        docType="estimate"
      />

      <POSubmitForApprovalModal
        api={api}
        po={submitPO}
        onClose={() => setSubmitPO(null)}
        onDone={() => { setSubmitPO(null); fetchPurchaseOrders?.(); }}
      />
    </div>
  );
}
